import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import activityLog from '../services/activityLog';
import { useTranslation } from 'react-i18next';
import { API_BASE } from '../services/api';
import {
  FiSearch, FiPlus, FiMoreHorizontal, FiLock, FiKey, 
  FiCreditCard, FiFile, FiDatabase, FiUser, FiUsers,
  FiGlobe, FiShield, FiFolder, FiStar, FiClock,
  FiEye, FiEyeOff, FiCopy, FiCheck, FiExternalLink,
  FiDownload, FiRefreshCw, FiLink, FiSmartphone, FiTrash2, FiX,
  FiBell, FiRepeat, FiAlertTriangle, FiChevronDown, FiChevronUp
} from 'react-icons/fi';
import { ReactComponent as PuzzleIcon } from '../assets/icons/puzzle.svg';
import extensionBridge from '../services/extensionBridge';
import { useToast } from '../hooks/useToast';
import useSessionExpiration from '../hooks/useSessionExpiration';
import Toast from './Toast';
import SessionInfo from './SessionInfo';
import LanguageSelector from './LanguageSelector';
import PasswordGenerator from './PasswordGenerator';
import ImportExport from './ImportExport';
import ExtensionUpdate, { compareVersions } from './ExtensionUpdate';
import OTPManager from './OTPManager';
import SmartCardBackup from './SmartCardBackup';
import RealtimeNotifications from './RealtimeNotifications';
import SiteIcon from './SiteIcon';
import './Vault.css';
import favicon from '../assets/images/favicon.svg';
import { withWsToken } from '../services/wsAuth';
import { apiErrorMessage } from '../utils/apiError';

// Same-origin by default: production proxies /ws to the backend from the main
// vhost (web.config "ProxyWebSocket"); api.<host> has no such rule and 404s.
const WS_URL = process.env.REACT_APP_WS_URL ||
  (window.location.protocol === 'https:' ? 'wss://' : 'ws://') +
  window.location.host + '/ws';

// Campos del item ssh en el formato nuevo (utils/sshKey.js). Esta pagina antigua solo edita el antiguo (sshKey).
const SSH_NEW_FIELDS = ['privateKey', 'publicKey', 'fingerprint', 'keyType', 'passphrase', 'comment'];

function Vault({ onLogout }) {
  const { t } = useTranslation();
  const { toasts, removeToast, success, error, warning, info } = useToast();
  
  // Hook para verificar expiración de sesión
  useSessionExpiration(() => {
    error(t('session.sessionExpired'));
    setTimeout(() => {
      if (onLogout) onLogout();
    }, 2000);
  });
  
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedVault, setSelectedVault] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedTag, setSelectedTag] = useState(null);
  const [items, setItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showNewItemModal, setShowNewItemModal] = useState(false);
  const [showNewVaultModal, setShowNewVaultModal] = useState(false);
  const [showNewTagModal, setShowNewTagModal] = useState(false);
  const [showItemForm, setShowItemForm] = useState(false);
  const [newItemType, setNewItemType] = useState('');
  const [newVaultName, setNewVaultName] = useState('');
  const [newTagName, setNewTagName] = useState('');
  const [formData, setFormData] = useState({
    title: '',
    username: '',
    password: '',
    url: '',
    notes: '',
    tags: [],
    vault: selectedVault,
    customFields: [],
    // Campos específicos por tipo
    cardNumber: '',
    cardHolder: '',
    expiryDate: '',
    cvv: '',
    cardType: '',
    cardPin: '',
    billingAddress: '',
    issuingBank: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    gender: '',
    birthDate: '',
    occupation: '',
    company: '',
    department: '',
    jobTitle: '',
    socialSecurity: '',
    passportNumber: '',
    licenseNumber: '',
    website: '',
    sshKey: '',
    apiKey: '',
    apiSecret: '',
    dbHost: '',
    dbPort: '',
    dbName: '',
    fileName: '',
    fileData: null,
    // Subscription fields
    provider: '',
    plan: '',
    cost: '',
    currency: 'EUR',
    billingCycle: 'monthly',
    renewalDate: '',
    startDate: '',
    subscriptionStatus: 'active',
    autoRenew: true,
    // Expiration & alerts
    expirationDate: '',
    alerts: []
  });
  const [vaults, setVaults] = useState([
    { id: 'all', name: t('sidebar.all'), icon: FiDatabase, isDefault: true }
  ]);
  const [tags, setTags] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  // New state for password generator and import/export
  const [showPasswordGenerator, setShowPasswordGenerator] = useState(false);
  const [showImportExport, setShowImportExport] = useState(false);
  const [showExtensionUpdate, setShowExtensionUpdate] = useState(false);
  const [hasExtensionUpdate, setHasExtensionUpdate] = useState(false);
  const [showOTPManager, setShowOTPManager] = useState(false);
  const [showSmartCardBackup, setShowSmartCardBackup] = useState(false);
  const [showRealtimeNotifications, setShowRealtimeNotifications] = useState(false);
  const [passwordGeneratorTarget, setPasswordGeneratorTarget] = useState(null);

  // Debounce search input to avoid filtering on every keystroke
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 250);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Discreet dot on the "Extension" button: one-shot check on mount, so the
  // user sees there's an update without having to open the modal first.
  useEffect(() => {
    let cancelled = false;
    Promise.all([
      fetch('/downloads/extension.json', { cache: 'no-store' }).then(r => (r.ok ? r.json() : null)).catch(() => null),
      extensionBridge.getInstalledExtensionVersion()
    ]).then(([latestInfo, installedVersion]) => {
      if (cancelled || !latestInfo?.version || !installedVersion) return;
      if (compareVersions(latestInfo.version, installedVersion) > 0) setHasExtensionUpdate(true);
    });
    return () => { cancelled = true; };
  }, []);

  // WebSocket for internal data/session change notifications
  useEffect(() => {
    let ws;
    let retryTimer;
    let disposed = false;
    let retryCount = 0;

    const connect = () => {
      if (disposed) return;
      ws = new WebSocket(withWsToken(WS_URL));
      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          const rfidId = localStorage.getItem('rfidId');
          if (!rfidId) return;

          if (message.event === 'data_updated' && message.data?.rfidId === rfidId) {
            setSyncStatus('syncing');
            if (fetchItemsRef.current) {
              fetchItemsRef.current(1, false, true);
            }
            const action = message.data?.action || 'update';
            const name = message.data?.name || '';
            if (action === 'create') info(t('dataList.saveSuccess') + (name ? `: ${name}` : ''));
            if (action === 'update') info(t('dataList.updateSuccess') + (name ? `: ${name}` : ''));
            if (action === 'delete') info(t('dataList.deleteSuccess'));
            if (action === 'wipe') info(t('dataList.wipeSuccess'));
          }

          if (message.event === 'session_config_updated' && message.data?.rfidId === rfidId) {
            info(t('session.configUpdated'));
          }
        } catch (e) {
          // Ignore parse errors
        }
      };

      ws.onopen = () => { retryCount = 0; };
      ws.onclose = () => {
        if (!disposed) {
          const delay = Math.min(3000 * Math.pow(2, retryCount), 60000); // Exponential backoff, max 60s
          retryCount++;
          retryTimer = setTimeout(connect, delay);
        }
      };
    };

    connect();

    return () => {
      disposed = true;
      if (retryTimer) clearTimeout(retryTimer);
      if (ws) {
        ws.onclose = null; // Prevent zombie reconnection
        ws.close();
      }
    };
  }, [info, t]);
  
  // State for visibility toggles in detail view
  const [visibleFields, setVisibleFields] = useState({});
  const [copiedField, setCopiedField] = useState(null);
  
  // State for multi-select delete
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [showHistoryPanel, setShowHistoryPanel] = useState(false);

  const categories = [
    { id: 'all', name: t('sidebar.all'), icon: FiDatabase },
    { id: 'favorites', name: t('sidebar.favorites'), icon: FiStar },
    { id: 'login', name: t('sidebar.logins'), icon: FiLock },
    { id: 'oauth', name: t('sidebar.oauth'), icon: FiUsers },
    { id: 'card', name: t('sidebar.cards'), icon: FiCreditCard },
    { id: 'identity', name: t('sidebar.identities'), icon: FiUser },
    { id: 'subscription', name: t('sidebar.subscriptions'), icon: FiRepeat },
    { id: 'note', name: t('sidebar.secureNotes'), icon: FiFile },
    { id: 'password', name: t('sidebar.passwords'), icon: FiKey },
    { id: 'ssh', name: t('sidebar.sshKeys'), icon: FiShield },
    { id: 'api', name: t('sidebar.apis'), icon: FiGlobe },
    { id: 'database', name: t('sidebar.databases'), icon: FiDatabase },
    { id: 'file', name: t('sidebar.files'), icon: FiFile }
  ];

  const itemTypes = [
    { value: 'login', label: t('dataEntry.newLogin'), icon: FiLock },
    { value: 'oauth', label: t('dataEntry.newOAuth'), icon: FiUsers },
    { value: 'note', label: t('dataEntry.newNote'), icon: FiFile },
    { value: 'card', label: t('dataEntry.newCard'), icon: FiCreditCard },
    { value: 'identity', label: t('dataEntry.newIdentity'), icon: FiUser },
    { value: 'subscription', label: t('dataEntry.newSubscription'), icon: FiRepeat },
    { value: 'password', label: t('dataEntry.newPassword'), icon: FiKey },
    { value: 'ssh', label: t('dataEntry.newSSH'), icon: FiShield },
    { value: 'api', label: t('dataEntry.newAPI'), icon: FiGlobe },
    { value: 'database', label: t('dataEntry.newDatabase'), icon: FiDatabase },
    { value: 'file', label: t('dataEntry.newFile'), icon: FiFile }
  ];

  // OAuth providers list
  const oauthProviders = [
    { value: 'google', label: 'Google', icon: '🔵' },
    { value: 'microsoft', label: 'Microsoft', icon: '🟦' },
    { value: 'github', label: 'GitHub', icon: '⬛' },
    { value: 'discord', label: 'Discord', icon: '🟣' },
    { value: 'facebook', label: 'Facebook', icon: '🔵' },
    { value: 'twitter', label: 'Twitter/X', icon: '⬛' },
    { value: 'apple', label: 'Apple', icon: '⚪' },
    { value: 'linkedin', label: 'LinkedIn', icon: '🔷' },
    { value: 'amazon', label: 'Amazon', icon: '🟠' },
    { value: 'twitch', label: 'Twitch', icon: '🟣' },
    { value: 'spotify', label: 'Spotify', icon: '🟢' },
    { value: 'slack', label: 'Slack', icon: '🟣' },
    { value: 'other', label: t('oauth.otherProvider'), icon: '🔗' }
  ];

  const filterItems = useCallback(() => {
    if (!Array.isArray(items) || items.length === 0) {
      setFilteredItems([]);
      return;
    }

    const isHiddenSystemItem = (item) => {
      const name = item?.name || item?.title || '';
      return item?.type === 'otp_vault' || name === '__otp_entries__';
    };

    let filtered = items.filter(item => !isHiddenSystemItem(item));

    // Filtrar por bóveda
    if (selectedVault !== 'all') {
      filtered = filtered.filter(item => item.vault === selectedVault);
    }

    // Filtrar por búsqueda (uses debounced value to avoid re-filtering on every keystroke)
    if (debouncedSearch) {
      const query = debouncedSearch.toLowerCase();
      filtered = filtered.filter(item =>
        item.title?.toLowerCase().includes(query) ||
        item.description?.toLowerCase().includes(query) ||
        item.type?.toLowerCase().includes(query) ||
        item.tags?.some(tag => tag.toLowerCase().includes(query))
      );
    }

    // Filtrar por categoría
    if (selectedCategory !== 'all') {
      if (selectedCategory === 'favorites') {
        filtered = filtered.filter(item => item.favorite);
      } else if (selectedCategory === 'oauth') {
        // OAuth filter: includes items with type 'oauth', loginMethod 'oauth', 
        // or login items with multipleUse enabled
        filtered = filtered.filter(item => 
          item.type === 'oauth' || 
          item.loginMethod === 'oauth' ||
          (item.type === 'login' && item.multipleUse && item.linkedType === 'oauth')
        );
      } else if (selectedCategory === 'login') {
        // Login filter: includes items with type 'login',
        // or oauth items with multipleUse enabled
        filtered = filtered.filter(item => 
          item.type === 'login' ||
          (item.type === 'oauth' && item.multipleUse && item.linkedType === 'login')
        );
      } else {
        filtered = filtered.filter(item => item.type === selectedCategory);
      }
    }

    // Filtrar por tag
    if (selectedTag) {
      filtered = filtered.filter(item => 
        Array.isArray(item.tags) && item.tags.includes(selectedTag)
      );
    }

    setFilteredItems(filtered);
  }, [items, debouncedSearch, selectedCategory, selectedVault, selectedTag]);

  const fetchVaults = async () => {
    try {
      const rfidId = localStorage.getItem('rfidId');
      const token = localStorage.getItem('authToken');
      
      if (!rfidId || !token) return;
      
      const response = await axios.get(`${API_BASE}/api/vaults?rfidId=${rfidId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.data.success && response.data.vaults) {
        const backendVaults = response.data.vaults.map(v => ({
          id: v.vaultId,
          name: v.name,
          icon: FiFolder,
          _id: v._id
        }));
        
        setVaults(prev => {
          const currentVaults = Array.isArray(prev) ? prev : [{ id: 'all', name: t('sidebar.all'), icon: FiDatabase, isDefault: true }];
          const defaultVaults = currentVaults.filter(v => v.isDefault);
          return [...defaultVaults, ...backendVaults];
        });
      }
    } catch (err) {
      console.error('Error al cargar bóvedas:', err);
    }
  };

  useEffect(() => {
    fetchVaults();
    fetchItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    filterItems();
  }, [filterItems]);

  // State for sync status
  const [syncStatus, setSyncStatus] = useState('idle'); // idle, syncing, synced, error
  const [syncChanges, setSyncChanges] = useState({ added: 0, modified: 0, deleted: 0 });
  
  // Pagination state for lazy loading
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMoreItems, setHasMoreItems] = useState(false);
  const [bulkLoadProgress, setBulkLoadProgress] = useState(null); // { loaded, total } or null
  const ITEMS_PER_PAGE = 50; // Balanced page size to avoid connection drops
  
  // Ref for infinite scroll container
  const scrollContainerRef = useRef(null);
  // Ref to store loadMoreItems function for scroll handler
  const loadMoreRef = useRef(null);
  // Ref for the sentinel element (triggers loading when visible)
  const loadMoreSentinelRef = useRef(null);
  // Ref to track items for cache saving (avoids dependency loops)
  const itemsRef = useRef([]);
  // Ref to store server cursor for pagination
  const lastCursorRef = useRef(null);
  // Ref to hold large file data outside of React state to avoid copy-on-spread
  const fileDataRef = useRef(null);
  
  // Keep itemsRef updated
  useEffect(() => {
    itemsRef.current = items;
  }, [items]);

  // Función para mapear datos del backend al formato esperado
  // includeHeavy=false mantiene el payload mínimo para evitar consumo excesivo de memoria en listados masivos.
  const mapRawData = useCallback((rawData, { includeHeavy = false } = {}) => {
    return rawData.map(item => {
      let parsedData = {};
      try {
        if (item.data && typeof item.data === 'string') {
          parsedData = JSON.parse(item.data);
        } else if (item.data && typeof item.data === 'object') {
          parsedData = item.data;
        }
      } catch (e) {
        console.warn('Error parsing data:', e);
      }

      const isOtpVault = parsedData?.type === 'otp_vault';
      const otpEntries = isOtpVault && Array.isArray(parsedData.entries) ? parsedData.entries : undefined;

      // Only store essential fields to reduce memory
      return {
        _id: item._id || item.id,
        id: item._id || item.id,
        title: item.name || item.title || t('vaultManager.untitled'),
        name: item.name || item.title || t('vaultManager.untitled'),
        type: parsedData.type || 'note',
        username: parsedData.username || '',
        email: parsedData.email || parsedData.username || '',
        // IMPORTANT: keep list payload light to avoid OOM; secrets are fetched on-demand in handleSelectItem
        password: includeHeavy ? (parsedData.password || '') : '',
        url: parsedData.url || '',
        vault: parsedData.vault,
        tags: item.tags || parsedData.tags || [],
        favorite: parsedData.favorite || false,
        loginMethod: parsedData.loginMethod,
        multipleUse: parsedData.multipleUse,
        linkedType: parsedData.linkedType,
        createdAt: item.createdAt,
        updatedAt: item.updatedAt,
        // Store other fields only if they exist
        ...(includeHeavy && parsedData.notes && { notes: parsedData.notes }),
        ...(includeHeavy && parsedData.cardNumber && { cardNumber: parsedData.cardNumber }),
        ...(includeHeavy && parsedData.totp && { totp: parsedData.totp }),
        ...(includeHeavy && otpEntries && { entries: otpEntries }),
        ...(isOtpVault && typeof parsedData.entriesCount === 'number' && { entriesCount: parsedData.entriesCount }),
        // Extended identity fields
        ...(includeHeavy && parsedData.firstName && { firstName: parsedData.firstName }),
        ...(includeHeavy && parsedData.lastName && { lastName: parsedData.lastName }),
        ...(includeHeavy && parsedData.phone && { phone: parsedData.phone }),
        ...(includeHeavy && parsedData.address && { address: parsedData.address }),
        ...(includeHeavy && parsedData.gender && { gender: parsedData.gender }),
        ...(includeHeavy && parsedData.birthDate && { birthDate: parsedData.birthDate }),
        ...(includeHeavy && parsedData.occupation && { occupation: parsedData.occupation }),
        ...(includeHeavy && parsedData.company && { company: parsedData.company }),
        ...(includeHeavy && parsedData.department && { department: parsedData.department }),
        ...(includeHeavy && parsedData.jobTitle && { jobTitle: parsedData.jobTitle }),
        ...(includeHeavy && parsedData.socialSecurity && { socialSecurity: parsedData.socialSecurity }),
        ...(includeHeavy && parsedData.passportNumber && { passportNumber: parsedData.passportNumber }),
        ...(includeHeavy && parsedData.licenseNumber && { licenseNumber: parsedData.licenseNumber }),
        ...(includeHeavy && parsedData.website && { website: parsedData.website }),
        // Extended card fields
        ...(includeHeavy && parsedData.cardHolder && { cardHolder: parsedData.cardHolder }),
        ...(includeHeavy && parsedData.expiryDate && { expiryDate: parsedData.expiryDate }),
        ...(includeHeavy && parsedData.cvv && { cvv: parsedData.cvv }),
        ...(includeHeavy && parsedData.cardType && { cardType: parsedData.cardType }),
        ...(includeHeavy && parsedData.cardPin && { cardPin: parsedData.cardPin }),
        ...(includeHeavy && parsedData.billingAddress && { billingAddress: parsedData.billingAddress }),
        ...(includeHeavy && parsedData.issuingBank && { issuingBank: parsedData.issuingBank }),
        // Subscription fields
        ...(includeHeavy && parsedData.provider && { provider: parsedData.provider }),
        ...(includeHeavy && parsedData.plan && { plan: parsedData.plan }),
        ...(includeHeavy && parsedData.cost && { cost: parsedData.cost }),
        ...(includeHeavy && parsedData.currency && { currency: parsedData.currency }),
        ...(includeHeavy && parsedData.billingCycle && { billingCycle: parsedData.billingCycle }),
        ...(includeHeavy && parsedData.renewalDate && { renewalDate: parsedData.renewalDate }),
        ...(includeHeavy && parsedData.startDate && { startDate: parsedData.startDate }),
        ...(includeHeavy && parsedData.subscriptionStatus && { subscriptionStatus: parsedData.subscriptionStatus }),
        ...(includeHeavy && parsedData.autoRenew !== undefined && { autoRenew: parsedData.autoRenew }),
        // SSH, API, DB fields
        ...(includeHeavy && parsedData.sshKey && { sshKey: parsedData.sshKey }),
        // Item ssh en el formato nuevo (fase 6): esta pagina antigua no lo edita, pero lo conserva entero al guardar.
        ...(includeHeavy && parsedData.type === 'ssh' && Object.fromEntries(SSH_NEW_FIELDS.filter((k) => parsedData[k]).map((k) => [k, parsedData[k]]))),
        ...(includeHeavy && parsedData.apiKey && { apiKey: parsedData.apiKey }),
        ...(includeHeavy && parsedData.apiSecret && { apiSecret: parsedData.apiSecret }),
        ...(includeHeavy && parsedData.dbHost && { dbHost: parsedData.dbHost }),
        ...(includeHeavy && parsedData.dbPort && { dbPort: parsedData.dbPort }),
        ...(includeHeavy && parsedData.dbName && { dbName: parsedData.dbName }),
        ...(includeHeavy && parsedData.fileName && { fileName: parsedData.fileName }),
        ...(includeHeavy && parsedData.fileData && { fileData: parsedData.fileData }),
        // OAuth fields
        ...(parsedData.oauthProvider && { oauthProvider: parsedData.oauthProvider }),
        ...(parsedData.oauthProviderName && { oauthProviderName: parsedData.oauthProviderName }),
        ...(parsedData.oauthEmail && { oauthEmail: parsedData.oauthEmail }),
        ...(parsedData.oauthUsername && { oauthUsername: parsedData.oauthUsername }),
        ...(parsedData.oauthType && { oauthType: parsedData.oauthType }),
        ...(parsedData.linkedOAuthId && { linkedOAuthId: parsedData.linkedOAuthId }),
        // Some flows (extension auto-detect) store provider fields under different keys
        ...(parsedData.provider && { provider: parsedData.provider }),
        ...(parsedData.providerName && { providerName: parsedData.providerName }),
        // Custom fields, change history, alerts, expiration
        ...(includeHeavy && Array.isArray(parsedData.customFields) && parsedData.customFields.length > 0 && { customFields: parsedData.customFields }),
        ...(includeHeavy && Array.isArray(parsedData.changeHistory) && parsedData.changeHistory.length > 0 && { changeHistory: parsedData.changeHistory }),
        ...(Array.isArray(parsedData.alerts) && parsedData.alerts.length > 0 && { alerts: parsedData.alerts }),
        ...(parsedData.expirationDate && { expirationDate: parsedData.expirationDate }),
        ...(parsedData.description && { description: parsedData.description })
      };
    });
  }, [t]);

  // ===== OAuth / Provider sign-in helpers =====

  const OAUTH_PROVIDER_DEFAULT_URLS = {
    google: 'https://accounts.google.com',
    microsoft: 'https://login.microsoftonline.com',
    github: 'https://github.com/login',
    discord: 'https://discord.com/login',
    facebook: 'https://www.facebook.com/login',
    twitter: 'https://twitter.com/i/flow/login',
    apple: 'https://appleid.apple.com',
    linkedin: 'https://www.linkedin.com/login',
    amazon: 'https://www.amazon.com/ap/signin',
    twitch: 'https://id.twitch.tv/oauth2/login',
    spotify: 'https://accounts.spotify.com/en/login',
    slack: 'https://slack.com/signin'
  };

  const OAUTH_PROVIDER_LOGIN_HOST_HINTS = {
    google: ['accounts.google.', 'google.com'],
    microsoft: ['login.microsoftonline.', 'login.live.', 'microsoft.com'],
    github: ['github.com'],
    discord: ['discord.com'],
    facebook: ['facebook.com'],
    twitter: ['twitter.com', 'x.com'],
    apple: ['appleid.apple.com', 'apple.com'],
    linkedin: ['linkedin.com'],
    amazon: ['amazon.'],
    twitch: ['twitch.tv', 'id.twitch.tv'],
    spotify: ['spotify.com'],
    slack: ['slack.com']
  };

  const safeHostFromUrl = (url) => {
    try {
      const urlObj = new URL(url.startsWith('http') ? url : `https://${url}`);
      return (urlObj.hostname || '').toLowerCase();
    } catch {
      return (url || '').toLowerCase();
    }
  };

  const findProviderLoginCredential = useCallback((provider, email) => {
    const p = (provider || '').toLowerCase();
    const e = (email || '').toLowerCase();
    if (!p || !e) return null;

    const all = Array.isArray(itemsRef.current) ? itemsRef.current : [];
    const hints = OAUTH_PROVIDER_LOGIN_HOST_HINTS[p] || [];

    // Prefer exact email match + provider host hint
    const exactMatches = all.filter(it => {
      if (it?.type !== 'login') return false;
      const user = (it.username || it.email || '').toLowerCase();
      if (user !== e) return false;
      const host = safeHostFromUrl(it.url || '');
      return hints.length === 0 ? true : hints.some(h => host.includes(h));
    });
    if (exactMatches.length > 0) return exactMatches[0];

    // Fallback: email match only
    const emailOnly = all.find(it => {
      if (it?.type !== 'login') return false;
      const user = (it.username || it.email || '').toLowerCase();
      return user === e;
    });
    return emailOnly || null;
  }, []);

  const findProviderLoginCredentialOnServer = useCallback(async (provider, email) => {
    const p = (provider || '').toLowerCase();
    const e = (email || '').toLowerCase();
    if (!p || !e) return null;

    const token = localStorage.getItem('authToken');
    const rfidId = localStorage.getItem('rfidId');
    if (!token || !rfidId) return null;

    const hints = OAUTH_PROVIDER_LOGIN_HOST_HINTS[p] || [];

    const LIMIT = 100;
    const MAX_PAGES = 15; // up to 1500 items scanned on-demand
    let nextCursor = null;
    let page = 1;

    for (let i = 0; i < MAX_PAGES; i++) {
      let url = `${API_BASE}/api/data/${rfidId}?page=${page}&limit=${LIMIT}&light=1`;
      if (nextCursor?.cursor_created_at && nextCursor?.cursor_id) {
        url += `&cursor_created_at=${encodeURIComponent(nextCursor.cursor_created_at)}&cursor_id=${encodeURIComponent(nextCursor.cursor_id)}`;
      }

      const resp = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 30000
      });

      const pageData = resp?.data?.data || [];
      if (!Array.isArray(pageData) || pageData.length === 0) return null;

      const mapped = mapRawData(pageData);
      const match = mapped.find(it => {
        if (it?.type !== 'login') return false;
        const user = (it.username || it.email || '').toLowerCase();
        if (user !== e) return false;
        const host = safeHostFromUrl(it.url || '');
        return hints.length === 0 ? true : hints.some(h => host.includes(h));
      });
      if (match) return match;

      nextCursor = resp?.data?.nextCursor || null;
      if (!nextCursor) {
        page += 1;
        if (page > (resp?.data?.totalPages || page)) return null;
      }
    }

    return null;
  }, [mapRawData]);

  const fetchFullItemById = useCallback(async (itemId) => {
    try {
      const token = localStorage.getItem('authToken');
      const rfidId = localStorage.getItem('rfidId');
      if (!token || !rfidId || !itemId) return null;

      const response = await axios.get(
        `${API_BASE}/api/data/${rfidId}/item/${itemId}`,
        { headers: { Authorization: `Bearer ${token}` }, timeout: 20000 }
      );
      const fullItem = mapRawData([response.data], { includeHeavy: true })[0];
      return fullItem || null;
    } catch (err) {
      console.warn('[Vault] No se pudo cargar detalle completo del item:', err?.message || err);
      return null;
    }
  }, [mapRawData]);

  const handleSelectItem = useCallback(async (item) => {
    setSelectedItem(item);
    const itemId = item?._id || item?.id;
    const fullItem = await fetchFullItemById(itemId);
    if (fullItem) setSelectedItem(fullItem);
  }, [fetchFullItemById]);

  const isHiddenSystemItem = useCallback((item) => {
    const name = item?.name || item?.title || '';
    return item?.type === 'otp_vault' || name === '__otp_entries__';
  }, []);

  const isHiddenTag = useCallback((tag) => {
    const normalized = (tag || '').toLowerCase();
    return ['otp', 'system', 'otp_vault', '__otp_entries__'].includes(normalized);
  }, []);

  const isFullyLoadedRef = useRef(false);
  const fetchItemsRef = useRef(null);

  const fetchItems = async (page = 1, append = false, forceFullLoad = false) => {
    try {
      const token = localStorage.getItem('authToken');
      const rfidId = localStorage.getItem('rfidId');
      
      if (!token || !rfidId) {
        setItems([]);
        setInitialLoading(false);
        return;
      }

      if (append) {
        setIsLoadingMore(true);
      }

      // Fetch from server — use cursor pagination on load-more to avoid slow OFFSET
      let fetchUrl = `${API_BASE}/api/data/${rfidId}?limit=${ITEMS_PER_PAGE}&light=1`;
      if (append && lastCursorRef.current?.cursor_created_at && lastCursorRef.current?.cursor_id) {
        fetchUrl += `&cursor_created_at=${encodeURIComponent(lastCursorRef.current.cursor_created_at)}&cursor_id=${encodeURIComponent(lastCursorRef.current.cursor_id)}`;
      } else {
        fetchUrl += `&page=${page}`;
      }
      const response = await axios.get(fetchUrl, { headers: { Authorization: `Bearer ${token}` } });
      
      const pageData = response.data.data || [];
      const mappedPageData = mapRawData(pageData);
      
      // Update pagination state
      setTotalPages(response.data.totalPages || 1);
      setTotalItems(response.data.totalCount || pageData.length);
      setHasMoreItems(response.data.hasMore || false);
      setCurrentPage(page);
      lastCursorRef.current = response.data.nextCursor || null;
      
      console.log(`[Fetch] Page ${page}: ${pageData.length} items, total: ${response.data.totalCount}, hasMore: ${response.data.hasMore}`);

      // Update items - append or replace
      if (append) {
        setItems(prev => {
          const existingIds = new Set(prev.map(i => i._id || i.id));
          const newItems = mappedPageData.filter(i => !existingIds.has(i._id || i.id));
          return newItems.length > 0 ? [...prev, ...newItems] : prev;
        });
      } else {
        setItems(mappedPageData);
        setInitialLoading(false);
      }

      // Extract tags
      const allTags = new Set(tags);
      mappedPageData.forEach(item => {
        if (isHiddenSystemItem(item)) return;
        if (Array.isArray(item.tags)) {
          item.tags.forEach(tag => allTags.add(tag));
        }
      });
      setTags(Array.from(allTags));

      setSyncStatus('synced');
      setIsLoadingMore(false);
      
    } catch (err) {
      console.error('Error loading items:', err);
      setSyncStatus('error');
      setIsLoadingMore(false);
      setInitialLoading(false);
      
      if (items.length === 0) {
        error(t('sync.errorLoading'));
      } else {
        warning(t('sync.usingCachedData'));
      }
    }
  };

  useEffect(() => {
    fetchItemsRef.current = fetchItems;
  }, [fetchItems]);

  // Smart load more - waits for previous request to complete
  const loadMoreItems = useCallback(async () => {
    if (isLoadingMore || !hasMoreItems) return;
    await fetchItems(currentPage + 1, true);
  }, [currentPage, isLoadingMore, hasMoreItems]);
  
  // Keep ref updated with latest loadMoreItems
  useEffect(() => {
    loadMoreRef.current = loadMoreItems;
  }, [loadMoreItems]);
  
  // Infinite scroll with IntersectionObserver - loads when sentinel is visible
  useEffect(() => {
    const sentinel = loadMoreSentinelRef.current;
    if (!sentinel) return;
    
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting && loadMoreRef.current) {
          loadMoreRef.current();
        }
      },
      {
        root: scrollContainerRef.current,
        rootMargin: '200px',
        threshold: 0
      }
    );
    
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMoreItems]);

  // Aggressive bulk loader — when items.length < totalItems, load ALL remaining pages fast
  // Fires once after first page loads and detects there are more items to fetch
  // Appends each page directly to state to avoid holding all data in a temp variable
  const bulkLoadingRef = useRef(false);
  const bulkLoadDisabledRef = useRef(false);
  useEffect(() => {
    // Only trigger when we have some items but not all, and not already loading
    if (isFullyLoadedRef.current || bulkLoadingRef.current) return;
    if (bulkLoadDisabledRef.current) return;
    if (!hasMoreItems || itemsRef.current.length === 0 || isLoadingMore) return;

    // Safety valve: for very large vaults, do NOT load everything in the background.
    // Let infinite scroll handle it progressively to avoid OOM/crashes.
    const initialLoadedForCheck = itemsRef.current.length;
    const serverTotalForCheck = totalItems || initialLoadedForCheck;
    const MAX_AGGRESSIVE_BULK_TOTAL_ITEMS = 500;
    if (serverTotalForCheck > MAX_AGGRESSIVE_BULK_TOTAL_ITEMS) {
      console.log(`[BulkLoad] Skipping aggressive bulk load (total=${serverTotalForCheck} > ${MAX_AGGRESSIVE_BULK_TOTAL_ITEMS})`);
      bulkLoadDisabledRef.current = true;
      return;
    }

    // We have partial data — start aggressive load
    bulkLoadingRef.current = true;
    const token = localStorage.getItem('authToken');
    const rfidId = localStorage.getItem('rfidId');
    if (!token || !rfidId) {
      bulkLoadingRef.current = false;
      return;
    }

    let cancelled = false;

    const loadAllRemaining = async () => {
      const initialLoaded = itemsRef.current.length;
      let serverTotal = totalItems || initialLoaded;
      console.log(`[BulkLoad] Starting: ${initialLoaded} loaded, ${serverTotal} total`);
      setBulkLoadProgress({ loaded: initialLoaded, total: serverTotal });
      let nextPage = Math.max(currentPage + 1, Math.floor(initialLoaded / ITEMS_PER_PAGE) + 1);
      let keepGoing = true;
      let pagesLoaded = 0;
      let nextCursor = lastCursorRef.current || null; // Continue from last cursor if available
      let cursorDisabled = false;
      const BULK_PAGE_SIZE = ITEMS_PER_PAGE;
      const PAGE_DELAY = 400; // ms between requests to avoid overwhelming server/proxy
      const MAX_RETRIES = 3;
      let loadedCount = initialLoaded;

      // Fast dedup without scanning the whole current list each page
      const seenIds = new Set((itemsRef.current || []).map(i => i._id || i.id));

      while (keepGoing && !cancelled) {
        let lastErr = null;
        let success = false;

        for (let attempt = 1; attempt <= MAX_RETRIES && !success && !cancelled; attempt++) {
          try {
            // Build URL — prefer cursor-based pagination, fall back to offset
            let url = `${API_BASE}/api/data/${rfidId}?page=${nextPage}&limit=${BULK_PAGE_SIZE}&light=1`;
            const canUseCursor = !cursorDisabled && nextCursor?.cursor_created_at && nextCursor?.cursor_id;
            if (canUseCursor) {
              url += `&cursor_created_at=${encodeURIComponent(nextCursor.cursor_created_at)}&cursor_id=${encodeURIComponent(nextCursor.cursor_id)}`;
            }

            const response = await axios.get(url, {
              headers: { Authorization: `Bearer ${token}` },
              timeout: 45000
            });
            const pageData = response.data.data || [];
            if (pageData.length === 0) { keepGoing = false; break; }

            const mapped = mapRawData(pageData);
            pagesLoaded++;

            // Append this page directly to state with deduplication
            const newItems = mapped.filter(i => {
              const id = i._id || i.id;
              if (!id) return false;
              if (seenIds.has(id)) return false;
              seenIds.add(id);
              return true;
            });
            if (newItems.length > 0) {
              setItems(prev => [...prev, ...newItems]);
            }
            setCurrentPage(nextPage);

            // Track cursor for next request
            nextCursor = response.data.nextCursor || null;

            serverTotal = response.data.totalCount || serverTotal;
            loadedCount += pageData.length;
            setBulkLoadProgress({ loaded: Math.min(loadedCount, serverTotal), total: serverTotal });

            if (loadedCount >= serverTotal || pageData.length === 0) {
              keepGoing = false;
            } else {
              nextPage++;
            }
            success = true;
          } catch (err) {
            lastErr = err;

            // If cursor pagination fails, fall back to plain page/offset mode.
            if (!cursorDisabled && nextCursor?.cursor_created_at && nextCursor?.cursor_id) {
              cursorDisabled = true;
            }

            const backoff = Math.min(1000 * Math.pow(2, attempt - 1), 8000); // 1s, 2s, 4s (max 8s)
            const status = err?.response?.status;
            const code = err?.code;
            console.warn(
              `[BulkLoad] Page ${nextPage} attempt ${attempt}/${MAX_RETRIES} failed:`,
              err?.message,
              status ? `(HTTP ${status})` : '',
              code ? `(code ${code})` : '',
              `— retrying in ${backoff}ms`
            );
            await new Promise(r => setTimeout(r, backoff));
          }
        }

        if (!success && cancelled) {
          keepGoing = false;
        } else if (!success) {
          const status = lastErr?.response?.status;
          const data = lastErr?.response?.data;
          console.error('[BulkLoad] All retries exhausted for page', nextPage, { status, data, message: lastErr?.message, code: lastErr?.code });
          keepGoing = false;
        }

        // Throttle: wait between successful requests to avoid ERR_CONNECTION_CLOSED
        if (keepGoing && !cancelled) {
          await new Promise(r => setTimeout(r, PAGE_DELAY));
        }
      }

      if (!cancelled) {
        // Extract tags from the final item list (outside of state updater to avoid anti-pattern)
        const finalItems = itemsRef.current;
        const allTags = new Set();
        finalItems.forEach(item => {
          if (isHiddenSystemItem(item)) return;
          if (Array.isArray(item.tags)) item.tags.forEach(tag => allTags.add(tag));
        });
        setTags(Array.from(allTags));
        const hasMore = loadedCount < serverTotal;
        setHasMoreItems(hasMore);
        isFullyLoadedRef.current = !hasMore;
        console.log(`[BulkLoad] Complete: ${pagesLoaded} pages loaded`);
      }

      setBulkLoadProgress(null);
      bulkLoadingRef.current = false;
    };

    // Small delay to let the UI render the first page
    const timer = setTimeout(loadAllRemaining, 300);
    return () => { cancelled = true; clearTimeout(timer); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMoreItems, totalItems]);


  // Background sync removed: state is refreshed via WebSocket data_updated event only.
  // No frontend cache, no periodic polling, no IndexedDB writes for vault data.

  const handleAddVault = async () => {
    if (!newVaultName.trim()) {
      warning(t('vaultManager.enterVaultName'));
      return;
    }
    
    const vaultId = newVaultName.toLowerCase().replace(/\s+/g, '-');
    
    // Verificar si ya existe
    if (vaults.find(v => v.id === vaultId)) {
      warning(t('vaultManager.vaultExists'));
      return;
    }
    
    try {
      const rfidId = localStorage.getItem('rfidId');
      const token = localStorage.getItem('authToken');
      
      const response = await axios.post(`${API_BASE}/api/vaults`, {
        rfidId,
        name: newVaultName,
        vaultId,
        icon: 'folder',
        color: '#667eea'
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.data.success) {
        const newVault = {
          id: response.data.vault.vaultId,
          name: response.data.vault.name,
          icon: FiFolder,
          _id: response.data.vault._id
        };
        
        setVaults([...vaults, newVault]);
        setNewVaultName('');
        setShowNewVaultModal(false);
        success(t('vaultManager.vaultCreatedSuccess', { name: newVaultName }));
      }
    } catch (err) {
      console.error('Error al crear bóveda:', err);
      error(apiErrorMessage(err, t, 'vaultManager.createError'));
    }
  };

  const handleAddTag = () => {
    if (!newTagName.trim()) {
      warning(t('vaultManager.enterTagName'));
      return;
    }
    
    if (!tags.includes(newTagName)) {
      setTags([...tags, newTagName]);
      success(t('vaultManager.tagCreatedSuccess', { name: newTagName }));
    } else {
      warning(t('vaultManager.tagExists'));
    }
    
    setNewTagName('');
    setShowNewTagModal(false);
  };

  const handleSelectItemType = (type) => {
    setNewItemType(type);
    setShowNewItemModal(false);
    setShowItemForm(true);
    fileDataRef.current = null; // Clear any stale file data
    // Si está en "Todos los elementos", usar personal por defecto
    // Si está en una bóveda específica, usar esa como sugerencia
    const initialVault = selectedVault === 'all' ? 'personal' : selectedVault;
    setFormData({
      title: '',
      username: '',
      password: '',
      url: '',
      notes: '',
      tags: [],
      vault: initialVault,
      customFields: [],
      cardNumber: '',
      cardHolder: '',
      expiryDate: '',
      cvv: '',
      cardType: '',
      cardPin: '',
      billingAddress: '',
      issuingBank: '',
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      address: '',
      gender: '',
      birthDate: '',
      occupation: '',
      company: '',
      department: '',
      jobTitle: '',
      socialSecurity: '',
      passportNumber: '',
      licenseNumber: '',
      website: '',
      sshKey: '',
      apiKey: '',
      apiSecret: '',
      dbHost: '',
      dbPort: '',
      dbName: '',
      fileName: '',
      fileData: null,
      // OAuth fields
      oauthProvider: '',
      oauthEmail: '',
      oauthUsername: '',
      // Multiple use switch
      multipleUse: false,
      linkedType: '',
      // Subscription fields
      provider: '',
      plan: '',
      cost: '',
      currency: 'EUR',
      billingCycle: 'monthly',
      renewalDate: '',
      startDate: '',
      subscriptionStatus: 'active',
      autoRenew: true,
      // Expiration & alerts
      expirationDate: '',
      alerts: []
    });
  };

  const handleEditItem = () => {
    if (!selectedItem) return;
    
    setNewItemType(selectedItem.type);
    setShowItemForm(true);
    setFormData({
      _id: selectedItem._id,
      title: selectedItem.title || '',
      username: selectedItem.username || '',
      password: selectedItem.password || '',
      url: selectedItem.url || '',
      notes: selectedItem.notes || '',
      tags: selectedItem.tags || [],
      vault: selectedItem.vault || 'personal',
      customFields: selectedItem.customFields || [],
      cardNumber: selectedItem.cardNumber || '',
      cardHolder: selectedItem.cardHolder || '',
      expiryDate: selectedItem.expiryDate || '',
      cvv: selectedItem.cvv || '',
      cardType: selectedItem.cardType || '',
      cardPin: selectedItem.cardPin || '',
      billingAddress: selectedItem.billingAddress || '',
      issuingBank: selectedItem.issuingBank || '',
      firstName: selectedItem.firstName || '',
      lastName: selectedItem.lastName || '',
      email: selectedItem.email || '',
      phone: selectedItem.phone || '',
      address: selectedItem.address || '',
      gender: selectedItem.gender || '',
      birthDate: selectedItem.birthDate || '',
      occupation: selectedItem.occupation || '',
      company: selectedItem.company || '',
      department: selectedItem.department || '',
      jobTitle: selectedItem.jobTitle || '',
      socialSecurity: selectedItem.socialSecurity || '',
      passportNumber: selectedItem.passportNumber || '',
      licenseNumber: selectedItem.licenseNumber || '',
      website: selectedItem.website || '',
      sshKey: selectedItem.sshKey || '',
      apiKey: selectedItem.apiKey || '',
      apiSecret: selectedItem.apiSecret || '',
      dbHost: selectedItem.dbHost || '',
      dbPort: selectedItem.dbPort || '',
      dbName: selectedItem.dbName || '',
      fileName: selectedItem.fileName || '',
      fileData: selectedItem.fileData || null,
      // OAuth fields
      oauthProvider: selectedItem.oauthProvider || selectedItem.provider || '',
      oauthEmail: selectedItem.oauthEmail || selectedItem.email || '',
      oauthUsername: selectedItem.oauthUsername || '',
      // Multiple use switch
      multipleUse: selectedItem.multipleUse || false,
      linkedType: selectedItem.linkedType || '',
      // Subscription fields
      provider: selectedItem.provider || '',
      plan: selectedItem.plan || '',
      cost: selectedItem.cost || '',
      currency: selectedItem.currency || 'EUR',
      billingCycle: selectedItem.billingCycle || 'monthly',
      renewalDate: selectedItem.renewalDate || '',
      startDate: selectedItem.startDate || '',
      subscriptionStatus: selectedItem.subscriptionStatus || 'active',
      autoRenew: selectedItem.autoRenew !== undefined ? selectedItem.autoRenew : true,
      // Expiration & alerts
      expirationDate: selectedItem.expirationDate || '',
      alerts: selectedItem.alerts || []
    });
  };

  const handleToggleFavorite = async () => {
    if (!selectedItem) return;

    try {
      const token = localStorage.getItem('authToken');
      const rfidId = localStorage.getItem('rfidId');

      const isFavorite = !selectedItem.favorite;

      // Preparar datos
      const dataToSave = { ...selectedItem, favorite: isFavorite };
      delete dataToSave._id;
      delete dataToSave.createdAt;
      delete dataToSave.updatedAt;
      delete dataToSave.__v;

      const itemData = {
        rfidId,
        name: selectedItem.title,
        description: selectedItem.title,
        data: dataToSave,
        tags: selectedItem.tags || [],
        itemId: selectedItem._id
      };

      await axios.post(`${API_BASE}/api/data/save`, itemData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Actualizar el selectedItem inmediatamente
      setSelectedItem({
        ...selectedItem,
        favorite: isFavorite
      });

      success(isFavorite ? t('vaultManager.addedToFavorites') : t('vaultManager.removedFromFavorites'));
      await fetchItems();
    } catch (err) {
      console.error('Error al actualizar favorito:', err);
      error(t('vaultManager.errorUpdateItem'));
    }
  };

  // Delete a single item
  const handleDeleteItem = async (item) => {
    try {
      const token = localStorage.getItem('authToken');
      const rfidId = localStorage.getItem('rfidId');
      
      await axios.delete(`${API_BASE}/api/data/${item._id}?rfidId=${rfidId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      success(t('dataList.deleteSuccess'));
      setShowDeleteConfirm(false);
      setItemToDelete(null);
      
      if (selectedItem?._id === item._id) {
        setSelectedItem(null);
      }
      
      await fetchItems();
    } catch (err) {
      console.error('Error deleting item:', err);
      error(t('dataList.errorDelete'));
    }
  };

  // Delete multiple selected items
  const handleDeleteSelected = async () => {
    if (selectedItems.size === 0) return;
    
    try {
      const token = localStorage.getItem('authToken');
      const rfidId = localStorage.getItem('rfidId');
      
      const deletePromises = Array.from(selectedItems).map(itemId =>
        axios.delete(`${API_BASE}/api/data/${itemId}?rfidId=${rfidId}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      );
      
      await Promise.all(deletePromises);
      
      success(t('dataList.deleteSuccess') + ` (${selectedItems.size})`);
      setSelectedItems(new Set());
      setIsSelectionMode(false);
      setShowDeleteConfirm(false);
      setSelectedItem(null);
      
      await fetchItems();
    } catch (err) {
      console.error('Error deleting items:', err);
      error(t('dataList.errorDelete'));
    }
  };

  // Delete ALL vault data
  const [showWipeConfirm, setShowWipeConfirm] = useState(false);
  const [wipingVault, setWipingVault] = useState(false);

  const handleDeleteAllVault = async () => {
    try {
      setWipingVault(true);
      const token = localStorage.getItem('authToken');
      const rfidId = localStorage.getItem('rfidId');

      const response = await axios.delete(`${API_BASE}/api/data/all?rfidId=${rfidId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      success(t('common.vaultWiped', { count: response.data.deletedCount }));
      setShowWipeConfirm(false);
      setSelectedItem(null);
      setSelectedItems(new Set());
      await fetchItems();
    } catch (err) {
      console.error('Error wiping vault:', err);
      error(t('common.vaultWipeError'));
    } finally {
      setWipingVault(false);
    }
  };

  // Toggle item selection
  const toggleItemSelection = (itemId, e) => {
    e.stopPropagation();
    setSelectedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  // Select/deselect all items
  const toggleSelectAll = () => {
    if (selectedItems.size === filteredItems.length) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(filteredItems.map(item => item._id)));
    }
  };

  // Exit selection mode
  const exitSelectionMode = () => {
    setIsSelectionMode(false);
    setSelectedItems(new Set());
  };

  const handleSaveItem = async () => {
    if (!formData.title.trim()) {
      warning(t('vaultManager.enterTitle'));
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      const rfidId = localStorage.getItem('rfidId');

      // Build change history entry if editing
      let changeHistory = [];
      if (formData._id && selectedItem) {
        const existingHistory = selectedItem.changeHistory || [];
        const changes = [];
        const fieldsToTrack = ['title', 'username', 'password', 'url', 'email', 'phone', 'address',
          'cardNumber', 'cardHolder', 'expiryDate', 'cvv', 'cardType', 'cardPin', 'billingAddress', 'issuingBank',
          'firstName', 'lastName', 'gender', 'birthDate', 'occupation', 'company', 'department', 'jobTitle',
          'socialSecurity', 'passportNumber', 'licenseNumber', 'website',
          'provider', 'plan', 'cost', 'currency', 'billingCycle', 'renewalDate', 'startDate', 'subscriptionStatus',
          'sshKey', 'apiKey', 'apiSecret', 'dbHost', 'dbPort', 'dbName',
          'oauthProvider', 'oauthEmail', 'oauthUsername', 'expirationDate', 'notes'];
        
        fieldsToTrack.forEach(field => {
          const oldVal = selectedItem[field] || '';
          const newVal = formData[field] || '';
          if (String(oldVal) !== String(newVal)) {
            changes.push({
              field,
              oldValue: field.toLowerCase().includes('password') || field === 'cvv' || field === 'cardPin' || field === 'socialSecurity' ? '••••••' : String(oldVal),
              newValue: field.toLowerCase().includes('password') || field === 'cvv' || field === 'cardPin' || field === 'socialSecurity' ? '••••••' : String(newVal)
            });
          }
        });

        if (changes.length > 0) {
          changeHistory = [...existingHistory, {
            date: new Date().toISOString(),
            changes
          }].slice(-50); // Keep last 50 history entries
        } else {
          changeHistory = existingHistory;
        }
      }

      // Preparar datos para guardar según el tipo
      let dataToSave = {
        type: newItemType,
        vault: formData.vault,
        tags: formData.tags,
        notes: formData.notes,
        customFields: formData.customFields,
        changeHistory,
        alerts: formData.alerts || [],
        expirationDate: formData.expirationDate || ''
      };

      // Agregar campos específicos según el tipo
      if (newItemType === 'login') {
        dataToSave = { 
          ...dataToSave, 
          username: formData.username, 
          password: formData.password, 
          url: formData.url,
          multipleUse: formData.multipleUse,
          linkedType: formData.multipleUse ? 'oauth' : ''
        };
      } else if (newItemType === 'oauth') {
        dataToSave = { 
          ...dataToSave, 
          oauthProvider: formData.oauthProvider,
          oauthEmail: formData.oauthEmail,
          oauthUsername: formData.oauthUsername,
          url: formData.url,
          oauthType: 'provider',
          multipleUse: formData.multipleUse,
          linkedType: formData.multipleUse ? 'login' : ''
        };
      } else if (newItemType === 'card') {
        dataToSave = { ...dataToSave, cardHolder: formData.cardHolder, cardNumber: formData.cardNumber, expiryDate: formData.expiryDate, cvv: formData.cvv, cardType: formData.cardType, cardPin: formData.cardPin, billingAddress: formData.billingAddress, issuingBank: formData.issuingBank };
      } else if (newItemType === 'identity') {
        dataToSave = { ...dataToSave, firstName: formData.firstName, lastName: formData.lastName, email: formData.email, phone: formData.phone, address: formData.address, gender: formData.gender, birthDate: formData.birthDate, occupation: formData.occupation, company: formData.company, department: formData.department, jobTitle: formData.jobTitle, socialSecurity: formData.socialSecurity, passportNumber: formData.passportNumber, licenseNumber: formData.licenseNumber, website: formData.website };
      } else if (newItemType === 'password') {
        dataToSave = { ...dataToSave, password: formData.password };
      } else if (newItemType === 'ssh') {
        // Un item ya en el formato nuevo conserva sus campos (el servidor limpia un sshKey vacio y da prioridad a uno editado).
        const keep = formData._id && selectedItem ? Object.fromEntries(SSH_NEW_FIELDS.filter((k) => selectedItem[k]).map((k) => [k, selectedItem[k]])) : {};
        dataToSave = { ...dataToSave, ...keep, sshKey: formData.sshKey };
      } else if (newItemType === 'api') {
        dataToSave = { ...dataToSave, apiKey: formData.apiKey, apiSecret: formData.apiSecret, url: formData.url };
      } else if (newItemType === 'database') {
        dataToSave = { ...dataToSave, dbHost: formData.dbHost, dbPort: formData.dbPort, dbName: formData.dbName, username: formData.username, password: formData.password };
      } else if (newItemType === 'file') {
        // Resolve file data from ref (where large base64 is stored to avoid state bloat)
        const resolvedFileData = formData.fileData === '__ref__' ? fileDataRef.current : formData.fileData;
        dataToSave = { ...dataToSave, fileName: formData.fileName, fileData: resolvedFileData };
      } else if (newItemType === 'subscription') {
        dataToSave = { ...dataToSave, provider: formData.provider, plan: formData.plan, cost: formData.cost, currency: formData.currency, billingCycle: formData.billingCycle, renewalDate: formData.renewalDate, startDate: formData.startDate, subscriptionStatus: formData.subscriptionStatus, autoRenew: formData.autoRenew, url: formData.url, username: formData.username, password: formData.password };
      }

      const itemData = {
        rfidId,
        name: formData.title,
        description: `${newItemType} - ${formData.title}`,
        data: dataToSave,
        tags: formData.tags
      };

      // Si tiene _id, es una edición
      if (formData._id) {
        itemData.itemId = formData._id;
      }

      const response = await axios.post(`${API_BASE}/api/data/save`, itemData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data && response.data.id) {
        success(formData._id ? t('vaultManager.itemUpdatedSuccess', { name: formData.title }) : t('vaultManager.itemSavedSuccess', { name: formData.title }));
        await fetchItems();
        setShowItemForm(false);
        setNewItemType('');
        setSelectedItem(null);
        fileDataRef.current = null; // Clear file data after save
        setFormData({
          title: '',
          username: '',
          password: '',
          url: '',
          notes: '',
          tags: [],
          vault: selectedVault === 'all' ? 'personal' : selectedVault,
          customFields: [],
          cardNumber: '',
          cardHolder: '',
          expiryDate: '',
          cvv: '',
          cardType: '',
          cardPin: '',
          billingAddress: '',
          issuingBank: '',
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          address: '',
          gender: '',
          birthDate: '',
          occupation: '',
          company: '',
          department: '',
          jobTitle: '',
          socialSecurity: '',
          passportNumber: '',
          licenseNumber: '',
          website: '',
          sshKey: '',
          apiKey: '',
          apiSecret: '',
          dbHost: '',
          dbPort: '',
          dbName: '',
          fileName: '',
          fileData: null,
          oauthProvider: '',
          oauthEmail: '',
          oauthUsername: '',
          multipleUse: false,
          linkedType: '',
          provider: '',
          plan: '',
          cost: '',
          currency: 'EUR',
          billingCycle: 'monthly',
          renewalDate: '',
          startDate: '',
          subscriptionStatus: 'active',
          autoRenew: true,
          expirationDate: '',
          alerts: []
        });
      }
    } catch (err) {
      console.error('Error al guardar item:', err);
      error(t('vaultManager.errorSavePrefix') + apiErrorMessage(err, t));
    }
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Password strength calculator (reusable across detail view and form)
  const calculatePasswordStrength = useCallback((pwd) => {
    if (!pwd) return { score: 0, label: '', color: '', percent: 0 };
    let score = 0;
    if (pwd.length >= 6) score += 1;
    if (pwd.length >= 8) score += 1;
    if (pwd.length >= 12) score += 1;
    if (pwd.length >= 16) score += 1;
    if (pwd.length >= 20) score += 1;
    if (/[a-z]/.test(pwd)) score += 1;
    if (/[A-Z]/.test(pwd)) score += 1;
    if (/[0-9]/.test(pwd)) score += 1;
    if (/[^a-zA-Z0-9]/.test(pwd)) score += 1;
    // Penalize patterns
    if (/(.)\1{2,}/.test(pwd)) score -= 1;
    if (/^[a-zA-Z]+$/.test(pwd) || /^[0-9]+$/.test(pwd)) score -= 1;
    score = Math.max(0, Math.min(score, 9));
    
    if (score <= 1) return { score: 1, label: t('dataEntry.strengthLabels.veryWeak'), color: '#ff2222', percent: 10 };
    if (score <= 2) return { score: 2, label: t('dataEntry.strengthLabels.weak'), color: '#ff4444', percent: 25 };
    if (score <= 3) return { score: 3, label: t('dataEntry.strengthLabels.fair'), color: '#ffaa00', percent: 40 };
    if (score <= 5) return { score: 4, label: t('dataEntry.strengthLabels.good'), color: '#88cc00', percent: 65 };
    if (score <= 7) return { score: 5, label: t('dataEntry.strengthLabels.strong'), color: '#00cc66', percent: 85 };
    return { score: 6, label: t('dataEntry.strengthLabels.veryStrong'), color: '#00cc66', percent: 100 };
  }, [t]);

  // Alert management for forms
  const handleAddAlert = () => {
    setFormData(prev => ({
      ...prev,
      alerts: [...(prev.alerts || []), { date: '', message: '' }]
    }));
  };

  const handleUpdateAlert = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      alerts: (prev.alerts || []).map((a, i) => i === index ? { ...a, [field]: value } : a)
    }));
  };

  const handleRemoveAlert = (index) => {
    setFormData(prev => ({
      ...prev,
      alerts: (prev.alerts || []).filter((_, i) => i !== index)
    }));
  };

  // Expiration alert checker for detail view
  const getExpirationStatus = useCallback((dateStr) => {
    if (!dateStr) return null;
    const now = new Date();
    let expDate;
    // Support MM/YY format
    if (/^\d{2}\/\d{2}$/.test(dateStr)) {
      const [mm, yy] = dateStr.split('/');
      expDate = new Date(2000 + parseInt(yy), parseInt(mm), 0); // last day of month
    } else {
      expDate = new Date(dateStr);
    }
    if (isNaN(expDate.getTime())) return null;
    const diffDays = Math.ceil((expDate - now) / (1000 * 60 * 60 * 24));
    if (diffDays < 0) return { status: 'expired', days: Math.abs(diffDays), color: '#ff4444' };
    if (diffDays === 0) return { status: 'today', days: 0, color: '#ff6600' };
    if (diffDays <= 30) return { status: 'upcoming', days: diffDays, color: '#ffaa00' };
    return { status: 'ok', days: diffDays, color: '#00cc66' };
  }, []);

  const handleAddTagToItem = (tag) => {
    const currentTags = Array.isArray(formData.tags) ? formData.tags : [];
    if (!currentTags.includes(tag)) {
      setFormData(prev => ({
        ...prev,
        tags: [...(Array.isArray(prev.tags) ? prev.tags : []), tag]
      }));
    }
  };

  const handleRemoveTagFromItem = (tag) => {
    setFormData(prev => ({
      ...prev,
      tags: Array.isArray(prev.tags) ? prev.tags.filter(t => t !== tag) : []
    }));
  };

  const handleAddCustomField = () => {
    setFormData(prev => ({
      ...prev,
      customFields: [...(Array.isArray(prev.customFields) ? prev.customFields : []), { label: '', value: '', type: 'text' }]
    }));
  };

  const handleUpdateCustomField = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      customFields: Array.isArray(prev.customFields) ? prev.customFields.map((cf, i) => 
        i === index ? { ...cf, [field]: value } : cf
      ) : []
    }));
  };

  const handleRemoveCustomField = (index) => {
    setFormData(prev => ({
      ...prev,
      customFields: Array.isArray(prev.customFields) ? prev.customFields.filter((_, i) => i !== index) : []
    }));
  };

  const handleFileChange = (e, isCustomField = false, customFieldIndex = null) => {
    const file = e.target.files[0];
    if (file) {
      // Enforce max file size (10 MB) to prevent OOM on base64 encoding
      const MAX_FILE_SIZE = 10 * 1024 * 1024;
      if (file.size > MAX_FILE_SIZE) {
        error(t('dataEntry.fileTooLarge'));
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        if (isCustomField) {
          handleUpdateCustomField(customFieldIndex, 'value', reader.result);
          handleUpdateCustomField(customFieldIndex, 'fileName', file.name);
        } else {
          // Store large base64 data in ref instead of state to prevent
          // re-copying on every form field change (avoids OOM for large files)
          fileDataRef.current = reader.result;
          setFormData(prev => ({
            ...prev,
            fileName: file.name,
            fileData: '__ref__' // Sentinel: actual data lives in fileDataRef
          }));
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const getItemIcon = (type, data = null) => {
    // If it's an OAuth type, return the OAuth provider icon
    if (type === 'oauth' || data?.loginMethod === 'oauth') {
      return FiUsers; // Use FiUsers for OAuth accounts
    }
    
    const iconMap = {
      login: FiLock,
      note: FiFile,
      card: FiCreditCard,
      identity: FiUser,
      password: FiKey,
      ssh: FiShield,
      api: FiGlobe,
      database: FiDatabase,
      file: FiFile,
      oauth: FiUsers
    };
    return iconMap[type] || FiFile;
  };

  // Sitio (URL o dominio) del que sale el icono de un item. Ya no se usa el `favicon` guardado en el item:
  // era una URL de Google o de la propia web que el navegador pedia directamente.
  const getIconSource = (urlOrItem, itemData = null) => {
    // Try multiple sources: url field, then title (might contain URL/domain)
    let url = typeof urlOrItem === 'string' ? urlOrItem : urlOrItem?.url;
    
    // If no URL, try to extract domain from title
    if (!url && itemData?.title) {
      const title = itemData.title.toLowerCase();
      // Check if title looks like a URL or domain
      if (title.includes('.com') || title.includes('.org') || title.includes('.net') || 
          title.includes('.io') || title.includes('.co') || title.includes('.uk') ||
          (title.includes('.') && !title.includes(' '))) {
        url = title;
      } else {
        // Try to match common service names
        const serviceMap = {
          'instagram': 'instagram.com',
          'facebook': 'facebook.com',
          'twitter': 'twitter.com',
          'github': 'github.com',
          'google': 'google.com',
          'microsoft': 'microsoft.com',
          'atlassian': 'atlassian.com',
          'discord': 'discord.com',
          'slack': 'slack.com',
          'linkedin': 'linkedin.com',
          'amazon': 'amazon.com',
          'netflix': 'netflix.com',
          'spotify': 'spotify.com',
          'dropbox': 'dropbox.com',
          'apple': 'apple.com',
          'adobe': 'adobe.com',
          'wordpress': 'wordpress.com',
          'stripe': 'stripe.com',
          'paypal': 'paypal.com',
          'twitch': 'twitch.tv',
          'reddit': 'reddit.com',
          'pinterest': 'pinterest.com',
          'tumblr': 'tumblr.com',
          'ezoic': 'ezoic.com',
          'giphy': 'giphy.com',
          'docusign': 'docusign.com',
          'insomnia': 'insomnia.rest',
          'ionos': 'ionos.com',
          'worldfirst': 'worldfirst.com',
          'farnell': 'farnell.com',
          'smtp': 'smtp.com',
          'ueni': 'ueni.com'
        };
        
        for (const [service, domain] of Object.entries(serviceMap)) {
          if (title.includes(service)) {
            url = domain;
            break;
          }
        }
      }
    }
    
    // El icono lo resuelve <SiteIcon> por el proxy del servidor (que reduce al dominio registrable).
    return url || null;
  };

  // Get OAuth provider icon
  const getOAuthProviderIcon = (provider) => {
    const icons = {
      google: '🔵',
      microsoft: '🟦',
      github: '⬛',
      discord: '🟣',
      facebook: '🔵',
      twitter: '⬛',
      apple: '⚪',
      linkedin: '🔷',
      amazon: '🟠',
      twitch: '🟣',
      spotify: '🟢',
      slack: '🟣'
    };
    return icons[provider] || '🔗';
  };

  const formatDate = (date) => {
    if (!date) return t('vault.dateUnknown', 'Unknown');
    
    const d = new Date(date);
    if (isNaN(d.getTime())) return t('vault.dateUnknown', 'Unknown');
    
    const now = new Date();
    const diff = now - d;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    
    if (days < 0) return t('vault.dateFuture', 'In the future');
    if (days === 0) return t('vault.dateToday', 'Today');
    if (days === 1) return t('vault.dateYesterday', 'Yesterday');
    if (days < 7) return t('vault.dateDaysAgo', '{{days}} days ago', { days });
    if (days < 30) return t('vault.dateWeeksAgo', '{{weeks}} weeks ago', { weeks: Math.floor(days / 7) });
    if (days < 365) return t('vault.dateMonthsAgo', '{{months}} months ago', { months: Math.floor(days / 30) });
    return t('vault.dateYearsAgo', '{{years}} years ago', { years: Math.floor(days / 365) });
  };

  // Copy to clipboard function
  const copyToClipboard = async (text, fieldName) => {
    try {
      await navigator.clipboard.writeText(text);
      if (fieldName === 'password') activityLog.record('copy_password', { itemId: selectedItem?._id || selectedItem?.id, itemName: selectedItem?.name || selectedItem?.title, origin: 'legacy vault' });
      setCopiedField(fieldName);
      success(t('dataList.copied'));
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
      error(t('errors.generic'));
    }
  };

  // Toggle field visibility
  const toggleFieldVisibility = (fieldName) => {
    setVisibleFields(prev => ({
      ...prev,
      [fieldName]: !prev[fieldName]
    }));
  };

  // Clear visible fields when item changes
  useEffect(() => {
    setVisibleFields({});
    setCopiedField(null);
  }, [selectedItem?._id]);

  // Open and Fill function
  const handleOpenAndFill = async (item) => {
    if (!item?.url) return;

    let resolved = item;
    // If we don't have a password (because list payload is light), fetch full detail now.
    if (!resolved?.password) {
      const itemId = resolved?._id || resolved?.id;
      const full = await fetchFullItemById(itemId);
      if (full) resolved = full;
    }
    
    // Create URL with encoded credentials as query params for our extension
    const url = new URL(item.url.startsWith('http') ? item.url : `https://${item.url}`);
    
    // Store credentials temporarily in sessionStorage for extension to pick up
    const credentials = {
      username: resolved.username || '',
      password: resolved.password || '',
      url: url.href,
      timestamp: Date.now()
    };
    
    sessionStorage.setItem('rfid_autofill', JSON.stringify(credentials));
    
    // Also dispatch a storage event to notify any listeners (including the extension content script)
    window.dispatchEvent(new StorageEvent('storage', {
      key: 'rfid_autofill',
      newValue: JSON.stringify(credentials)
    }));
    
    // Small delay to allow the extension content script to sync the data to chrome.storage
    // before opening the new tab
    setTimeout(() => {
      // Open the URL in a new tab
      window.open(url.href, '_blank');
    }, 300);
    
    // Show info message
    info(t('dataEntry.openAndFillMessage'));
  };

  // Open service page and trigger OAuth provider button (requires provider login credentials)
  const handleOpenAndOAuthLogin = async (item) => {
    const originUrl = item?.url;
    if (!originUrl) return;

    const provider = item?.oauthProvider || item?.provider;
    const providerEmail = item?.oauthEmail || item?.email || item?.username;
    let providerLoginLite = findProviderLoginCredential(provider, providerEmail);
    if (!providerLoginLite) {
      providerLoginLite = await findProviderLoginCredentialOnServer(provider, providerEmail);
    }

    if (!providerLoginLite) {
      warning(
        t('oauth.missingProviderLogin')
      );
      return;
    }

    const providerLoginId = providerLoginLite?._id || providerLoginLite?.id;
    const providerLogin = await fetchFullItemById(providerLoginId);
    if (!providerLogin) {
      warning(t('errors.generic'));
      return;
    }

    if (!providerLogin.password) {
      warning(
        t('oauth.missingProviderPassword')
      );
      return;
    }

    const providerUrl = providerLogin.url || OAUTH_PROVIDER_DEFAULT_URLS[(provider || '').toLowerCase()] || 'https://accounts.google.com';
    const credentials = {
      username: providerLogin.username || providerLogin.email || providerEmail || '',
      password: providerLogin.password || '',
      url: providerUrl,
      timestamp: Date.now()
    };

    const oauthStart = {
      originUrl: originUrl,
      provider: (provider || '').toLowerCase(),
      providerEmail: providerEmail || '',
      timestamp: Date.now()
    };

    // Store both: provider creds for the provider page + oauth-start for the service page
    sessionStorage.setItem('rfid_autofill', JSON.stringify(credentials));
    sessionStorage.setItem('rfid_oauth_start', JSON.stringify(oauthStart));

    // Notify listeners (extension content script on web app)
    window.dispatchEvent(new StorageEvent('storage', {
      key: 'rfid_autofill',
      newValue: JSON.stringify(credentials)
    }));
    window.dispatchEvent(new StorageEvent('storage', {
      key: 'rfid_oauth_start',
      newValue: JSON.stringify(oauthStart)
    }));

    setTimeout(() => {
      window.open(originUrl.startsWith('http') ? originUrl : `https://${originUrl}`, '_blank');
    }, 300);

    info(t('oauth.openAndSignInMessage'));
  };

  // Open provider login page directly (requires provider login credentials)
  const handleOpenProviderAccountLogin = async (item) => {
    const provider = item?.oauthProvider || item?.provider;
    const providerEmail = item?.oauthEmail || item?.email || item?.username;
    let providerLoginLite = findProviderLoginCredential(provider, providerEmail);
    if (!providerLoginLite) {
      providerLoginLite = await findProviderLoginCredentialOnServer(provider, providerEmail);
    }

    if (!providerLoginLite) {
      warning(
        t('oauth.missingProviderLogin')
      );
      return;
    }

    const providerLoginId = providerLoginLite?._id || providerLoginLite?.id;
    const providerLogin = await fetchFullItemById(providerLoginId);
    if (!providerLogin) {
      warning(t('errors.generic'));
      return;
    }

    if (!providerLogin.password) {
      warning(
        t('oauth.missingProviderPassword')
      );
      return;
    }

    const providerUrl = providerLogin.url || OAUTH_PROVIDER_DEFAULT_URLS[(provider || '').toLowerCase()] || 'https://accounts.google.com';
    const credentials = {
      username: providerLogin.username || providerLogin.email || providerEmail || '',
      password: providerLogin.password || '',
      url: providerUrl,
      timestamp: Date.now()
    };

    sessionStorage.setItem('rfid_autofill', JSON.stringify(credentials));
    window.dispatchEvent(new StorageEvent('storage', {
      key: 'rfid_autofill',
      newValue: JSON.stringify(credentials)
    }));

    setTimeout(() => {
      window.open(providerUrl, '_blank');
    }, 300);

    info(t('oauth.openProviderMessage'));
  };

  // Password generator callback
  const handlePasswordGenerated = (password) => {
    if (passwordGeneratorTarget) {
      if (passwordGeneratorTarget.startsWith('custom_')) {
        const index = parseInt(passwordGeneratorTarget.replace('custom_', ''));
        handleUpdateCustomField(index, 'value', password);
      } else {
        handleFormChange(passwordGeneratorTarget, password);
      }
    }
    setShowPasswordGenerator(false);
    setPasswordGeneratorTarget(null);
  };

  // Open password generator for a specific field
  const openPasswordGenerator = (targetField = 'password') => {
    setPasswordGeneratorTarget(targetField);
    setShowPasswordGenerator(true);
  };

  return (
    <div className="vault-container">
      {/* Toast notifications */}
      <div className="toast-container">
        {toasts.map(toast => (
          <Toast
            key={toast.id}
            message={toast.message}
            type={toast.type}
            duration={toast.duration}
            onClose={() => removeToast(toast.id)}
          />
        ))}
      </div>

      {/* Overlay para cerrar sidebar en móvil */}
      {sidebarOpen && <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)}></div>}
      
      {/* Sidebar izquierda */}
      <aside className={`vault-sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <div className="vault-profile">
            <div className="profile-avatar">
              <img src={favicon} alt="" style={{ width: '23px', height: '23px', verticalAlign: 'middle' }} />
            </div>
            <div className="profile-info">
              <h3>{t('app.title')}</h3>
              <LanguageSelector />
            </div>
          </div>
          <SessionInfo onLogout={onLogout} />
        </div>

        <div className="sidebar-section">
          <div className="sidebar-section-header">
            <span>{t('vaultManager.title').toUpperCase()}</span>
            <button className="btn-icon-small" onClick={() => setShowNewVaultModal(true)}>
              <FiPlus size={14} />
            </button>
          </div>
          {Array.isArray(vaults) && vaults.map(vault => (
            <button
              key={vault.id}
              className={`sidebar-item ${selectedVault === vault.id ? 'active' : ''}`}
              onClick={() => {
                setSelectedVault(vault.id);
                if (window.innerWidth <= 768) setSidebarOpen(false);
              }}
            >
              <vault.icon size={18} />
              <span>{vault.name}</span>
            </button>
          ))}
        </div>

        <div className="sidebar-section">
          <div className="sidebar-section-header">
            <span>{t('vault.categories.all').toUpperCase()}</span>
          </div>
            {categories.map(cat => (
            <button
              key={cat.id}
              className={`sidebar-item ${selectedCategory === cat.id ? 'active' : ''}`}
              onClick={() => {
                setSelectedCategory(cat.id);
                if (window.innerWidth <= 768) setSidebarOpen(false);
              }}
            >
              <cat.icon size={18} />
              <span>{cat.name}</span>
              {cat.id === 'favorites' && Array.isArray(items) && (
                <span className="item-count">{items.filter(i => !isHiddenSystemItem(i) && i.favorite).length}</span>
              )}
            </button>
          ))}
        </div>

        {Array.isArray(tags) && tags.length > 0 && (
          <div className="sidebar-section">
            <div className="sidebar-section-header">
              <span>{t('sidebar.tags').toUpperCase()}</span>
            </div>
            {tags.filter(tag => !isHiddenTag(tag)).map(tag => (
              <button
                key={tag}
                className={`sidebar-item ${selectedTag === tag ? 'active' : ''}`}
                onClick={() => {
                  setSelectedTag(selectedTag === tag ? null : tag);
                  if (window.innerWidth <= 768) setSidebarOpen(false);
                }}
              >
                <span className="tag-dot"></span>
                <span>{tag}</span>
                <span className="item-count">
                  {items.filter(i => !isHiddenSystemItem(i) && Array.isArray(i.tags) && i.tags.includes(tag) && !isHiddenTag(tag)).length}
                </span>
              </button>
            ))}
          </div>
        )}
      </aside>

      {/* Lista de items central */}
      <div className="vault-items-list">
        <div className="items-header">
          <button className="btn-menu-toggle" onClick={() => setSidebarOpen(!sidebarOpen)}>
            <FiMoreHorizontal size={20} />
          </button>
          <div className="search-bar">
            <FiSearch />
            <input
              type="text"
              placeholder={t('vault.searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <div className="header-actions">
            {isSelectionMode ? (
              <>
                <button 
                  className="btn-header-action"
                  onClick={toggleSelectAll}
                  title={selectedItems.size === filteredItems.length ? t('common.deselectAll') : t('common.selectAll')}
                >
                  {selectedItems.size === filteredItems.length ? <FiCheck size={18} /> : <FiDatabase size={18} />}
                </button>
                <button 
                  className="btn-header-action btn-danger"
                  onClick={() => setShowDeleteConfirm(true)}
                  disabled={selectedItems.size === 0}
                  title={t('common.delete')}
                >
                  <FiTrash2 size={18} />
                  {selectedItems.size > 0 && <span className="badge">{selectedItems.size}</span>}
                </button>
                <button 
                  className="btn-header-action"
                  onClick={exitSelectionMode}
                  title={t('common.cancel')}
                >
                  <FiX size={18} />
                </button>
              </>
            ) : (
              <>
                <button 
                  className="btn-header-action"
                  onClick={() => setIsSelectionMode(true)}
                  title={t('common.select')}
                >
                  <FiCheck size={18} />
                </button>
                <button 
                  className="btn-header-action btn-otp" 
                  onClick={() => setShowOTPManager(true)}
                  title={t('otp.title')}
                >
                  <FiSmartphone size={18} />
                </button>
                <button 
                  className="btn-header-action" 
                  onClick={() => setShowPasswordGenerator(true)}
                  title={t('passwordGenerator.title')}
                >
                  <FiKey size={18} />
                </button>
                <button
                  className="btn-header-action"
                  onClick={() => setShowImportExport(true)}
                  title={t('importExport.title')}
                >
                  <FiDownload size={18} />
                </button>
                <button
                  className="btn-header-action btn-extension"
                  onClick={() => setShowExtensionUpdate(true)}
                  title={t('extension.title')}
                >
                  <PuzzleIcon width={18} height={18} />
                  {hasExtensionUpdate && <span className="update-dot" />}
                </button>
                <button
                  className="btn-header-action"
                  onClick={() => setShowSmartCardBackup(true)}
                  title={t('smartcard.title')}
                >
                  <FiCreditCard size={18} />
                </button>
                <button 
                  className="btn-header-action btn-notifications" 
                  onClick={() => setShowRealtimeNotifications(true)}
                  title={t('notifications.title')}
                >
                  <FiBell size={18} />
                </button>
                <button 
                  className="btn-header-action btn-danger-wipe" 
                  onClick={() => setShowWipeConfirm(true)}
                  title={t('common.deleteAllVault')}
                >
                  <FiTrash2 size={18} />
                </button>
                <button className="btn-new-item" onClick={() => setShowNewItemModal(true)}>
                  <FiPlus size={18} />
                  <span>{t('vault.addNew')}</span>
                </button>
              </>
            )}
          </div>
        </div>

        <div className="items-content" ref={scrollContainerRef}>
          {initialLoading ? (
            <div className="vault-initial-loading">
              <div className="vault-loading-spinner"></div>
              <p>{t('common.loading') || 'Loading'}...</p>
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="empty-state">
              <FiFolder size={48} />
              <h3>{t('vault.noData')}</h3>
              <p>{t('vault.noDataDesc')}</p>
            </div>
          ) : (
            <div className="items-grid">
              {filteredItems.map(item => {
                const ItemIcon = getItemIcon(item.type, item);
                const hasOAuth = item.loginMethod === 'oauth' || item.type === 'oauth';
                const iconSource = getIconSource(item.url, item);
                const isItemSelected = selectedItems.has(item._id);
                return (
                  <div
                    key={item._id}
                    className={`item-card ${selectedItem?._id === item._id ? 'selected' : ''} ${hasOAuth ? 'oauth-item' : ''} ${isItemSelected ? 'multi-selected' : ''}`}
                    onClick={() => isSelectionMode ? toggleItemSelection(item._id, { stopPropagation: () => {} }) : handleSelectItem(item)}
                  >
                    {isSelectionMode && (
                      <div 
                        className={`item-checkbox ${isItemSelected ? 'checked' : ''}`}
                        onClick={(e) => toggleItemSelection(item._id, e)}
                      >
                        {isItemSelected && <FiCheck size={14} />}
                      </div>
                    )}
                    <div className="item-icon">
                      <SiteIcon
                        url={iconSource}
                        className="item-favicon"
                        fallback={(
                          <span className="item-icon-fallback">
                            <ItemIcon size={20} />
                          </span>
                        )}
                      />
                      {hasOAuth && (
                        <span className="oauth-badge" title={item.oauthProviderName || item.providerName}>
                          {getOAuthProviderIcon(item.oauthProvider || item.provider)}
                        </span>
                      )}
                    </div>
                    <div className="item-info">
                      <h4>{item.title || t('vaultManager.untitled')}</h4>
                      <p className="item-subtitle">
                        {hasOAuth && (
                          <span className="oauth-indicator">
                            <FiLink size={10} /> {item.oauthProviderName || item.providerName || 'OAuth'}
                            {' • '}
                          </span>
                        )}
                        {item.username || item.description || item.type}
                      </p>
                      <span className="item-date">
                        <FiClock size={12} />
                        {formatDate(item.createdAt)}
                      </span>
                    </div>
                    {item.favorite && (
                      <FiStar className="item-favorite" size={16} />
                    )}
                  </div>
                );
              })}
              
              {/* Infinite scroll sentinel & loading indicator */}
              {hasMoreItems && (
                <div ref={loadMoreSentinelRef} className="load-more-container">
                  {isLoadingMore ? (
                    <div className="infinite-scroll-loader">
                      <span className="loading-spinner-small"></span>
                      <span>{t('common.loading')}...</span>
                    </div>
                  ) : (
                    <div className="infinite-scroll-loader">
                      <span>{t('common.scrollForMore')}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          
          {/* Total count - always visible at bottom */}
          {items.length > 0 && (
            <div className="items-count">
              <span>{items.length} / {totalItems}</span>
              {bulkLoadProgress && (
                <>
                  <div className="bulk-load-bar">
                    <div className="bulk-load-bar-fill" style={{ width: `${Math.min(100, Math.round((bulkLoadProgress.loaded / bulkLoadProgress.total) * 100))}%` }} />
                  </div>
                  <span className="bulk-load-text">
                    {t('common.loading') || 'Cargando'} {bulkLoadProgress.loaded} / {bulkLoadProgress.total}...
                  </span>
                </>
              )}
              {!bulkLoadProgress && hasMoreItems && <span className="items-count-more"> • {t('common.moreAvailable') || 'hay más disponibles'}</span>}
            </div>
          )}
        </div>
      </div>

      {/* Panel de detalles derecha */}
      {selectedItem && (
        <div className="vault-details">
          <div className="details-header">
            <div className="details-header-buttons">
              <button className="btn-primary-small" onClick={handleEditItem}>{t('common.edit')}</button>
              <button 
                className="btn-danger-small" 
                onClick={() => { setItemToDelete(selectedItem); setShowDeleteConfirm(true); }}
              >
                <FiTrash2 size={14} /> {t('common.delete')}
              </button>
            </div>
            <div className="details-header-actions">
            <button 
              className="btn-icon" 
              onClick={handleToggleFavorite}
              style={{color: selectedItem.favorite ? '#ffd700' : 'inherit'}}
              title={selectedItem.favorite ? t('vaultManager.removedFromFavorites') : t('vaultManager.addedToFavorites')}
            >
              {selectedItem.favorite ? '★' : '☆'}
            </button>
            <button className="btn-icon" onClick={() => setSelectedItem(null)}>
              <FiX />
            </button>
            </div>
          </div>

          <div className="details-content">
            <div className="details-title-section">
              <div className="details-icon">
                <SiteIcon
                  url={getIconSource(selectedItem.url, selectedItem)}
                  className="details-favicon"
                  fallback={React.createElement(getItemIcon(selectedItem.type), { size: 32 })}
                />
              </div>
              <h2>{selectedItem.title || t('vaultManager.untitled')}</h2>
              <p className="details-subtitle">{t(`vault.categories.${selectedItem.type}`) || selectedItem.type}</p>
            </div>

            <div className="details-fields">
              {/* OAuth Provider Info */}
              {(selectedItem.loginMethod === 'oauth' || selectedItem.type === 'oauth') && (
                <div className="oauth-provider-info">
                  <div className="oauth-provider-icon">
                    {getOAuthProviderIcon(selectedItem.oauthProvider || selectedItem.provider)}
                  </div>
                  <div className="oauth-provider-details">
                    <h5>{selectedItem.type === 'oauth' ? t('oauth.providerAccount') : t('oauth.loginMethod')}</h5>
                    <p>
                      {selectedItem.oauthProviderName || selectedItem.providerName || 'OAuth'}
                      {selectedItem.oauthEmail && ` • ${selectedItem.oauthEmail}`}
                    </p>
                  </div>
                </div>
              )}

              {/* Username field */}
              {selectedItem.username && (
                <div className="detail-field">
                  <label>{t('dataEntry.username')}</label>
                  <div className="field-value field-with-actions">
                    <span>{selectedItem.username}</span>
                    <div className="field-actions">
                      <button 
                        className={`field-action-btn ${copiedField === 'username' ? 'copied' : ''}`}
                        onClick={() => copyToClipboard(selectedItem.username, 'username')}
                        title={t('dataList.copy')}
                      >
                        {copiedField === 'username' ? <FiCheck size={14} /> : <FiCopy size={14} />}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Password field - with visibility toggle */}
              {selectedItem.password && (
                <div className="detail-field">
                  <label>{t('dataEntry.password')}</label>
                  <div className="field-value field-with-actions sensitive">
                    <input
                      type={visibleFields.password ? 'text' : 'password'}
                      value={selectedItem.password}
                      readOnly
                      className="field-input-readonly"
                    />
                    <div className="field-actions">
                      <button 
                        className="field-action-btn"
                        onClick={() => toggleFieldVisibility('password')}
                        title={visibleFields.password ? t('dataEntry.hide') : t('dataEntry.show')}
                      >
                        {visibleFields.password ? <FiEyeOff size={14} /> : <FiEye size={14} />}
                      </button>
                      <button 
                        className={`field-action-btn ${copiedField === 'password' ? 'copied' : ''}`}
                        onClick={() => copyToClipboard(selectedItem.password, 'password')}
                        title={t('dataList.copy')}
                      >
                        {copiedField === 'password' ? <FiCheck size={14} /> : <FiCopy size={14} />}
                      </button>
                    </div>
                  </div>
                  {/* Password strength indicator */}
                  {(() => {
                    const strength = calculatePasswordStrength(selectedItem.password);
                    return (
                      <div className="password-strength-inline">
                        <div className="password-strength-bar-container">
                          <div className="password-strength-bar-fill" style={{ width: `${strength.percent}%`, backgroundColor: strength.color }} />
                        </div>
                        <span className="password-strength-label" style={{ color: strength.color }}>{strength.label}</span>
                      </div>
                    );
                  })()}
                </div>
              )}

              {/* URL field with Open and Fill button */}
              {selectedItem.url && (
                <div className="detail-field">
                  <label>{t('dataEntry.url')}</label>
                  <div className="field-value field-with-actions url-field">
                    <a href={selectedItem.url} target="_blank" rel="noopener noreferrer" className="url-link">
                      {selectedItem.url}
                    </a>
                    <div className="field-actions">
                      <button 
                        className={`field-action-btn ${copiedField === 'url' ? 'copied' : ''}`}
                        onClick={() => copyToClipboard(selectedItem.url, 'url')}
                        title={t('dataList.copy')}
                      >
                        {copiedField === 'url' ? <FiCheck size={14} /> : <FiCopy size={14} />}
                      </button>
                      <button 
                        className="field-action-btn open-fill-btn"
                        onClick={() => handleOpenAndFill(selectedItem)}
                        title={t('dataEntry.openAndFill')}
                      >
                        <FiExternalLink size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Card fields */}
              {selectedItem.type === 'card' && (
                <>
                  {selectedItem.cardHolder && (
                    <div className="detail-field">
                      <label>{t('dataEntry.cardHolder')}</label>
                      <div className="field-value field-with-actions">
                        <span>{selectedItem.cardHolder}</span>
                        <div className="field-actions">
                          <button 
                            className={`field-action-btn ${copiedField === 'cardHolder' ? 'copied' : ''}`}
                            onClick={() => copyToClipboard(selectedItem.cardHolder, 'cardHolder')}
                            title={t('dataList.copy')}
                          >
                            {copiedField === 'cardHolder' ? <FiCheck size={14} /> : <FiCopy size={14} />}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                  {selectedItem.cardNumber && (
                    <div className="detail-field">
                      <label>{t('dataEntry.cardNumber')}</label>
                      <div className="field-value field-with-actions sensitive">
                        <input
                          type={visibleFields.cardNumber ? 'text' : 'password'}
                          value={selectedItem.cardNumber}
                          readOnly
                          className="field-input-readonly"
                        />
                        <div className="field-actions">
                          <button 
                            className="field-action-btn"
                            onClick={() => toggleFieldVisibility('cardNumber')}
                            title={visibleFields.cardNumber ? t('dataEntry.hide') : t('dataEntry.show')}
                          >
                            {visibleFields.cardNumber ? <FiEyeOff size={14} /> : <FiEye size={14} />}
                          </button>
                          <button 
                            className={`field-action-btn ${copiedField === 'cardNumber' ? 'copied' : ''}`}
                            onClick={() => copyToClipboard(selectedItem.cardNumber, 'cardNumber')}
                            title={t('dataList.copy')}
                          >
                            {copiedField === 'cardNumber' ? <FiCheck size={14} /> : <FiCopy size={14} />}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                  {selectedItem.expiryDate && (
                    <div className="detail-field">
                      <label>{t('dataEntry.expiryDate')}</label>
                      <div className="field-value field-with-actions">
                        <span>{selectedItem.expiryDate}</span>
                        <div className="field-actions">
                          <button 
                            className={`field-action-btn ${copiedField === 'expiryDate' ? 'copied' : ''}`}
                            onClick={() => copyToClipboard(selectedItem.expiryDate, 'expiryDate')}
                            title={t('dataList.copy')}
                          >
                            {copiedField === 'expiryDate' ? <FiCheck size={14} /> : <FiCopy size={14} />}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                  {selectedItem.cvv && (
                    <div className="detail-field">
                      <label>{t('dataEntry.cvv')}</label>
                      <div className="field-value field-with-actions sensitive">
                        <input
                          type={visibleFields.cvv ? 'text' : 'password'}
                          value={selectedItem.cvv}
                          readOnly
                          className="field-input-readonly"
                        />
                        <div className="field-actions">
                          <button 
                            className="field-action-btn"
                            onClick={() => toggleFieldVisibility('cvv')}
                            title={visibleFields.cvv ? t('dataEntry.hide') : t('dataEntry.show')}
                          >
                            {visibleFields.cvv ? <FiEyeOff size={14} /> : <FiEye size={14} />}
                          </button>
                          <button 
                            className={`field-action-btn ${copiedField === 'cvv' ? 'copied' : ''}`}
                            onClick={() => copyToClipboard(selectedItem.cvv, 'cvv')}
                            title={t('dataList.copy')}
                          >
                            {copiedField === 'cvv' ? <FiCheck size={14} /> : <FiCopy size={14} />}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                  {selectedItem.cardType && (
                    <div className="detail-field">
                      <label>{t('dataEntry.cardType')}</label>
                      <div className="field-value field-with-actions">
                        <span>{selectedItem.cardType}</span>
                        <div className="field-actions">
                          <button className={`field-action-btn ${copiedField === 'cardType' ? 'copied' : ''}`} onClick={() => copyToClipboard(selectedItem.cardType, 'cardType')} title={t('dataList.copy')}>
                            {copiedField === 'cardType' ? <FiCheck size={14} /> : <FiCopy size={14} />}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                  {selectedItem.cardPin && (
                    <div className="detail-field">
                      <label>{t('dataEntry.cardPin')}</label>
                      <div className="field-value field-with-actions sensitive">
                        <input type={visibleFields.cardPin ? 'text' : 'password'} value={selectedItem.cardPin} readOnly className="field-input-readonly" />
                        <div className="field-actions">
                          <button className="field-action-btn" onClick={() => toggleFieldVisibility('cardPin')}>{visibleFields.cardPin ? <FiEyeOff size={14} /> : <FiEye size={14} />}</button>
                          <button className={`field-action-btn ${copiedField === 'cardPin' ? 'copied' : ''}`} onClick={() => copyToClipboard(selectedItem.cardPin, 'cardPin')} title={t('dataList.copy')}>{copiedField === 'cardPin' ? <FiCheck size={14} /> : <FiCopy size={14} />}</button>
                        </div>
                      </div>
                    </div>
                  )}
                  {selectedItem.issuingBank && (
                    <div className="detail-field">
                      <label>{t('dataEntry.issuingBank')}</label>
                      <div className="field-value"><span>{selectedItem.issuingBank}</span></div>
                    </div>
                  )}
                  {selectedItem.billingAddress && (
                    <div className="detail-field">
                      <label>{t('dataEntry.billingAddress')}</label>
                      <div className="field-value"><span>{selectedItem.billingAddress}</span></div>
                    </div>
                  )}
                  {/* Expiry date alert */}
                  {selectedItem.expiryDate && (() => {
                    const exp = getExpirationStatus(selectedItem.expiryDate);
                    return exp && exp.status !== 'ok' ? (
                      <div className="expiration-alert" style={{ borderLeftColor: exp.color }}>
                        <FiAlertTriangle size={14} style={{ color: exp.color }} />
                        <span style={{ color: exp.color }}>
                          {exp.status === 'expired' ? t('dataEntry.expiredDaysAgo', { days: exp.days })
                            : exp.status === 'today' ? t('dataEntry.expiresToday')
                            : t('dataEntry.daysUntilExpiry', { days: exp.days })}
                        </span>
                      </div>
                    ) : null;
                  })()}
                </>
              )}

              {/* Identity fields */}
              {selectedItem.type === 'identity' && (
                <>
                  {(selectedItem.firstName || selectedItem.lastName) && (
                    <div className="detail-field">
                      <label>{t('dataEntry.fullName')}</label>
                      <div className="field-value field-with-actions">
                        <span>{[selectedItem.firstName, selectedItem.lastName].filter(Boolean).join(' ')}</span>
                        <div className="field-actions">
                          <button 
                            className={`field-action-btn ${copiedField === 'fullName' ? 'copied' : ''}`}
                            onClick={() => copyToClipboard([selectedItem.firstName, selectedItem.lastName].filter(Boolean).join(' '), 'fullName')}
                            title={t('dataList.copy')}
                          >
                            {copiedField === 'fullName' ? <FiCheck size={14} /> : <FiCopy size={14} />}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                  {selectedItem.email && (
                    <div className="detail-field">
                      <label>{t('dataEntry.email')}</label>
                      <div className="field-value field-with-actions">
                        <span>{selectedItem.email}</span>
                        <div className="field-actions">
                          <button 
                            className={`field-action-btn ${copiedField === 'email' ? 'copied' : ''}`}
                            onClick={() => copyToClipboard(selectedItem.email, 'email')}
                            title={t('dataList.copy')}
                          >
                            {copiedField === 'email' ? <FiCheck size={14} /> : <FiCopy size={14} />}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                  {selectedItem.phone && (
                    <div className="detail-field">
                      <label>{t('dataEntry.phone')}</label>
                      <div className="field-value field-with-actions">
                        <span>{selectedItem.phone}</span>
                        <div className="field-actions">
                          <button 
                            className={`field-action-btn ${copiedField === 'phone' ? 'copied' : ''}`}
                            onClick={() => copyToClipboard(selectedItem.phone, 'phone')}
                            title={t('dataList.copy')}
                          >
                            {copiedField === 'phone' ? <FiCheck size={14} /> : <FiCopy size={14} />}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                  {selectedItem.address && (
                    <div className="detail-field">
                      <label>{t('dataEntry.address')}</label>
                      <div className="field-value field-with-actions">
                        <span>{selectedItem.address}</span>
                        <div className="field-actions">
                          <button 
                            className={`field-action-btn ${copiedField === 'address' ? 'copied' : ''}`}
                            onClick={() => copyToClipboard(selectedItem.address, 'address')}
                            title={t('dataList.copy')}
                          >
                            {copiedField === 'address' ? <FiCheck size={14} /> : <FiCopy size={14} />}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                  {selectedItem.gender && (
                    <div className="detail-field">
                      <label>{t('dataEntry.gender')}</label>
                      <div className="field-value"><span>{selectedItem.gender}</span></div>
                    </div>
                  )}
                  {selectedItem.birthDate && (
                    <div className="detail-field">
                      <label>{t('dataEntry.birthDate')}</label>
                      <div className="field-value"><span>{selectedItem.birthDate}</span></div>
                    </div>
                  )}
                  {selectedItem.occupation && (
                    <div className="detail-field">
                      <label>{t('dataEntry.occupation')}</label>
                      <div className="field-value"><span>{selectedItem.occupation}</span></div>
                    </div>
                  )}
                  {selectedItem.company && (
                    <div className="detail-field">
                      <label>{t('dataEntry.company')}</label>
                      <div className="field-value"><span>{selectedItem.company}</span></div>
                    </div>
                  )}
                  {selectedItem.department && (
                    <div className="detail-field">
                      <label>{t('dataEntry.department')}</label>
                      <div className="field-value"><span>{selectedItem.department}</span></div>
                    </div>
                  )}
                  {selectedItem.jobTitle && (
                    <div className="detail-field">
                      <label>{t('dataEntry.jobTitle')}</label>
                      <div className="field-value"><span>{selectedItem.jobTitle}</span></div>
                    </div>
                  )}
                  {selectedItem.socialSecurity && (
                    <div className="detail-field">
                      <label>{t('dataEntry.socialSecurity')}</label>
                      <div className="field-value field-with-actions sensitive">
                        <input type={visibleFields.socialSecurity ? 'text' : 'password'} value={selectedItem.socialSecurity} readOnly className="field-input-readonly" />
                        <div className="field-actions">
                          <button className="field-action-btn" onClick={() => toggleFieldVisibility('socialSecurity')}>{visibleFields.socialSecurity ? <FiEyeOff size={14} /> : <FiEye size={14} />}</button>
                          <button className={`field-action-btn ${copiedField === 'socialSecurity' ? 'copied' : ''}`} onClick={() => copyToClipboard(selectedItem.socialSecurity, 'socialSecurity')}>{copiedField === 'socialSecurity' ? <FiCheck size={14} /> : <FiCopy size={14} />}</button>
                        </div>
                      </div>
                    </div>
                  )}
                  {selectedItem.passportNumber && (
                    <div className="detail-field">
                      <label>{t('dataEntry.passportNumber')}</label>
                      <div className="field-value field-with-actions">
                        <span>{selectedItem.passportNumber}</span>
                        <div className="field-actions">
                          <button className={`field-action-btn ${copiedField === 'passportNumber' ? 'copied' : ''}`} onClick={() => copyToClipboard(selectedItem.passportNumber, 'passportNumber')}>{copiedField === 'passportNumber' ? <FiCheck size={14} /> : <FiCopy size={14} />}</button>
                        </div>
                      </div>
                    </div>
                  )}
                  {selectedItem.licenseNumber && (
                    <div className="detail-field">
                      <label>{t('dataEntry.licenseNumber')}</label>
                      <div className="field-value field-with-actions">
                        <span>{selectedItem.licenseNumber}</span>
                        <div className="field-actions">
                          <button className={`field-action-btn ${copiedField === 'licenseNumber' ? 'copied' : ''}`} onClick={() => copyToClipboard(selectedItem.licenseNumber, 'licenseNumber')}>{copiedField === 'licenseNumber' ? <FiCheck size={14} /> : <FiCopy size={14} />}</button>
                        </div>
                      </div>
                    </div>
                  )}
                  {selectedItem.website && (
                    <div className="detail-field">
                      <label>{t('dataEntry.website')}</label>
                      <div className="field-value field-with-actions url-field">
                        <a href={selectedItem.website} target="_blank" rel="noopener noreferrer" className="url-link">{selectedItem.website}</a>
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* SSH Key */}
              {selectedItem.type === 'ssh' && selectedItem.sshKey && (
                <div className="detail-field">
                  <label>{t('dataEntry.sshKey')}</label>
                  <div className="field-value field-with-actions sensitive">
                    <textarea
                      readOnly
                      value={visibleFields.sshKey ? selectedItem.sshKey : '••••••••••••••••••••'}
                      className="field-textarea-readonly"
                    />
                    <div className="field-actions">
                      <button 
                        className="field-action-btn"
                        onClick={() => toggleFieldVisibility('sshKey')}
                        title={visibleFields.sshKey ? t('dataEntry.hide') : t('dataEntry.show')}
                      >
                        {visibleFields.sshKey ? <FiEyeOff size={14} /> : <FiEye size={14} />}
                      </button>
                      <button 
                        className={`field-action-btn ${copiedField === 'sshKey' ? 'copied' : ''}`}
                        onClick={() => copyToClipboard(selectedItem.sshKey, 'sshKey')}
                        title={t('dataList.copy')}
                      >
                        {copiedField === 'sshKey' ? <FiCheck size={14} /> : <FiCopy size={14} />}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* API fields */}
              {selectedItem.type === 'api' && (
                <>
                  {selectedItem.apiKey && (
                    <div className="detail-field">
                      <label>{t('dataEntry.apiKey')}</label>
                      <div className="field-value field-with-actions sensitive">
                        <input
                          type={visibleFields.apiKey ? 'text' : 'password'}
                          value={selectedItem.apiKey}
                          readOnly
                          className="field-input-readonly"
                        />
                        <div className="field-actions">
                          <button 
                            className="field-action-btn"
                            onClick={() => toggleFieldVisibility('apiKey')}
                            title={visibleFields.apiKey ? t('dataEntry.hide') : t('dataEntry.show')}
                          >
                            {visibleFields.apiKey ? <FiEyeOff size={14} /> : <FiEye size={14} />}
                          </button>
                          <button 
                            className={`field-action-btn ${copiedField === 'apiKey' ? 'copied' : ''}`}
                            onClick={() => copyToClipboard(selectedItem.apiKey, 'apiKey')}
                            title={t('dataList.copy')}
                          >
                            {copiedField === 'apiKey' ? <FiCheck size={14} /> : <FiCopy size={14} />}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                  {selectedItem.apiSecret && (
                    <div className="detail-field">
                      <label>{t('dataEntry.apiSecret')}</label>
                      <div className="field-value field-with-actions sensitive">
                        <input
                          type={visibleFields.apiSecret ? 'text' : 'password'}
                          value={selectedItem.apiSecret}
                          readOnly
                          className="field-input-readonly"
                        />
                        <div className="field-actions">
                          <button 
                            className="field-action-btn"
                            onClick={() => toggleFieldVisibility('apiSecret')}
                            title={visibleFields.apiSecret ? t('dataEntry.hide') : t('dataEntry.show')}
                          >
                            {visibleFields.apiSecret ? <FiEyeOff size={14} /> : <FiEye size={14} />}
                          </button>
                          <button 
                            className={`field-action-btn ${copiedField === 'apiSecret' ? 'copied' : ''}`}
                            onClick={() => copyToClipboard(selectedItem.apiSecret, 'apiSecret')}
                            title={t('dataList.copy')}
                          >
                            {copiedField === 'apiSecret' ? <FiCheck size={14} /> : <FiCopy size={14} />}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* Database fields */}
              {selectedItem.type === 'database' && (
                <>
                  {selectedItem.dbHost && (
                    <div className="detail-field">
                      <label>{t('dataEntry.host')}</label>
                      <div className="field-value field-with-actions">
                        <span>{selectedItem.dbHost}</span>
                        <div className="field-actions">
                          <button 
                            className={`field-action-btn ${copiedField === 'dbHost' ? 'copied' : ''}`}
                            onClick={() => copyToClipboard(selectedItem.dbHost, 'dbHost')}
                            title={t('dataList.copy')}
                          >
                            {copiedField === 'dbHost' ? <FiCheck size={14} /> : <FiCopy size={14} />}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                  {selectedItem.dbPort && (
                    <div className="detail-field">
                      <label>{t('dataEntry.port')}</label>
                      <div className="field-value field-with-actions">
                        <span>{selectedItem.dbPort}</span>
                        <div className="field-actions">
                          <button 
                            className={`field-action-btn ${copiedField === 'dbPort' ? 'copied' : ''}`}
                            onClick={() => copyToClipboard(selectedItem.dbPort, 'dbPort')}
                            title={t('dataList.copy')}
                          >
                            {copiedField === 'dbPort' ? <FiCheck size={14} /> : <FiCopy size={14} />}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                  {selectedItem.dbName && (
                    <div className="detail-field">
                      <label>{t('dataEntry.database')}</label>
                      <div className="field-value field-with-actions">
                        <span>{selectedItem.dbName}</span>
                        <div className="field-actions">
                          <button 
                            className={`field-action-btn ${copiedField === 'dbName' ? 'copied' : ''}`}
                            onClick={() => copyToClipboard(selectedItem.dbName, 'dbName')}
                            title={t('dataList.copy')}
                          >
                            {copiedField === 'dbName' ? <FiCheck size={14} /> : <FiCopy size={14} />}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* Subscription fields */}
              {selectedItem.type === 'subscription' && (
                <>
                  {selectedItem.provider && (
                    <div className="detail-field">
                      <label>{t('dataEntry.provider')}</label>
                      <div className="field-value"><span>{selectedItem.provider}</span></div>
                    </div>
                  )}
                  {selectedItem.plan && (
                    <div className="detail-field">
                      <label>{t('dataEntry.plan')}</label>
                      <div className="field-value"><span>{selectedItem.plan}</span></div>
                    </div>
                  )}
                  {selectedItem.cost && (
                    <div className="detail-field">
                      <label>{t('dataEntry.cost')}</label>
                      <div className="field-value"><span>{selectedItem.cost} {selectedItem.currency || 'EUR'}</span></div>
                    </div>
                  )}
                  {selectedItem.billingCycle && (
                    <div className="detail-field">
                      <label>{t('dataEntry.billingCycle')}</label>
                      <div className="field-value"><span>{t(`dataEntry.billingCycles.${selectedItem.billingCycle}`) || selectedItem.billingCycle}</span></div>
                    </div>
                  )}
                  {selectedItem.subscriptionStatus && (
                    <div className="detail-field">
                      <label>{t('dataEntry.status')}</label>
                      <div className="field-value">
                        <span className={`subscription-status-badge status-${selectedItem.subscriptionStatus}`}>
                          {t(`dataEntry.subscriptionStatuses.${selectedItem.subscriptionStatus}`) || selectedItem.subscriptionStatus}
                        </span>
                      </div>
                    </div>
                  )}
                  {selectedItem.startDate && (
                    <div className="detail-field">
                      <label>{t('dataEntry.startDate')}</label>
                      <div className="field-value"><span>{selectedItem.startDate}</span></div>
                    </div>
                  )}
                  {selectedItem.renewalDate && (
                    <div className="detail-field">
                      <label>{t('dataEntry.renewalDate')}</label>
                      <div className="field-value"><span>{selectedItem.renewalDate}</span></div>
                      {(() => {
                        const exp = getExpirationStatus(selectedItem.renewalDate);
                        return exp && (exp.status === 'expired' || exp.status === 'today' || exp.status === 'upcoming') ? (
                          <div className="expiration-alert" style={{ borderLeftColor: exp.color }}>
                            <FiAlertTriangle size={14} style={{ color: exp.color }} />
                            <span style={{ color: exp.color }}>
                              {exp.status === 'expired' ? t('dataEntry.expiredDaysAgo', { days: exp.days })
                                : exp.status === 'today' ? t('dataEntry.expiresToday')
                                : t('dataEntry.daysUntilExpiry', { days: exp.days })}
                            </span>
                          </div>
                        ) : null;
                      })()}
                    </div>
                  )}
                  {selectedItem.autoRenew !== undefined && (
                    <div className="detail-field">
                      <label>{t('dataEntry.autoRenew')}</label>
                      <div className="field-value"><span>{selectedItem.autoRenew ? '✓' : '✗'}</span></div>
                    </div>
                  )}
                </>
              )}

              {/* General expiration alert */}
              {selectedItem.expirationDate && (() => {
                const exp = getExpirationStatus(selectedItem.expirationDate);
                return (
                  <div className="detail-field">
                    <label>{t('dataEntry.expirationDate')}</label>
                    <div className="field-value"><span>{selectedItem.expirationDate}</span></div>
                    {exp && exp.status !== 'ok' && (
                      <div className="expiration-alert" style={{ borderLeftColor: exp.color }}>
                        <FiAlertTriangle size={14} style={{ color: exp.color }} />
                        <span style={{ color: exp.color }}>
                          {exp.status === 'expired' ? t('dataEntry.expiredDaysAgo', { days: exp.days })
                            : exp.status === 'today' ? t('dataEntry.expiresToday')
                            : t('dataEntry.daysUntilExpiry', { days: exp.days })}
                        </span>
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Scheduled alerts */}
              {Array.isArray(selectedItem.alerts) && selectedItem.alerts.length > 0 && (
                <div className="detail-field alerts-section">
                  <label><FiBell size={14} /> {t('dataEntry.alerts')}</label>
                  <div className="alerts-list">
                    {selectedItem.alerts.map((alert, idx) => {
                      const exp = getExpirationStatus(alert.date);
                      return (
                        <div key={idx} className="alert-item" style={{ borderLeftColor: exp?.color || '#888' }}>
                          <div className="alert-date">{alert.date}</div>
                          <div className="alert-message">{alert.message}</div>
                          {exp && (
                            <span className="alert-badge" style={{ color: exp.color }}>
                              {exp.status === 'expired' ? t('dataEntry.alertExpired')
                                : exp.status === 'today' ? t('dataEntry.expiresToday')
                                : exp.status === 'upcoming' ? t('dataEntry.alertUpcoming')
                                : t('dataEntry.alertActive')}
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Custom fields */}
              {Array.isArray(selectedItem.customFields) && selectedItem.customFields.length > 0 && (
                <>
                  {selectedItem.customFields.map((field, index) => (
                    <div key={index} className="detail-field">
                      <label>{field.label || `${t('dataEntry.customFields')} ${index + 1}`}</label>
                      <div className="field-value field-with-actions">
                        {field.type === 'password' ? (
                          <input
                            type={visibleFields[`custom_${index}`] ? 'text' : 'password'}
                            value={field.value}
                            readOnly
                            className="field-input-readonly"
                          />
                        ) : (
                          <span>{field.value}</span>
                        )}
                        <div className="field-actions">
                          {field.type === 'password' && (
                            <button 
                              className="field-action-btn"
                              onClick={() => toggleFieldVisibility(`custom_${index}`)}
                              title={visibleFields[`custom_${index}`] ? t('dataEntry.hide') : t('dataEntry.show')}
                            >
                              {visibleFields[`custom_${index}`] ? <FiEyeOff size={14} /> : <FiEye size={14} />}
                            </button>
                          )}
                          <button 
                            className={`field-action-btn ${copiedField === `custom_${index}` ? 'copied' : ''}`}
                            onClick={() => copyToClipboard(field.value, `custom_${index}`)}
                            title={t('dataList.copy')}
                          >
                            {copiedField === `custom_${index}` ? <FiCheck size={14} /> : <FiCopy size={14} />}
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </>
              )}

              {/* Notes */}
              {selectedItem.notes && (
                <div className="detail-field">
                  <label>{t('dataEntry.notes')}</label>
                  <div className="field-value">
                    <span className="notes-content">{selectedItem.notes}</span>
                  </div>
                </div>
              )}

              {/* Description */}
              {selectedItem.description && selectedItem.description !== selectedItem.notes && (
                <div className="detail-field">
                  <label>{t('vaultManager.description')}</label>
                  <div className="field-value">
                    <span>{selectedItem.description}</span>
                  </div>
                </div>
              )}

              {/* Tags */}
              {Array.isArray(selectedItem.tags) && selectedItem.tags.length > 0 && (
                <div className="detail-field">
                  <label>{t('dataEntry.tags')}</label>
                  <div className="field-value tags-display">
                    {selectedItem.tags.map((tag, index) => (
                      <span key={index} className="tag-badge-display">{tag}</span>
                    ))}
                  </div>
                </div>
              )}

              {/* Change History */}
              {Array.isArray(selectedItem.changeHistory) && selectedItem.changeHistory.length > 0 && (
                <div className="detail-field history-section">
                  <button 
                    className="history-toggle-btn"
                    onClick={() => setShowHistoryPanel(!showHistoryPanel)}
                  >
                    <FiClock size={14} />
                    <span>{t('dataEntry.changeHistory')} ({selectedItem.changeHistory.length})</span>
                    {showHistoryPanel ? <FiChevronUp size={14} /> : <FiChevronDown size={14} />}
                  </button>
                  {showHistoryPanel && (
                    <div className="history-panel">
                      {selectedItem.changeHistory.slice().reverse().map((entry, idx) => (
                        <div key={idx} className="history-entry">
                          <div className="history-date">
                            <FiClock size={12} />
                            {new Date(entry.date).toLocaleString()}
                          </div>
                          <div className="history-changes">
                            {entry.changes.map((change, cIdx) => (
                              <div key={cIdx} className="history-change">
                                <span className="history-field-name">{change.field}</span>
                                {change.action === 'added' && (
                                  <span className="history-action added">{t('dataEntry.fieldAdded')}</span>
                                )}
                                {change.action === 'removed' && (
                                  <span className="history-action removed">{t('dataEntry.fieldRemovedHistory')}</span>
                                )}
                                {change.action === 'changed' && (
                                  <>
                                    <span className="history-old-value">{change.from}</span>
                                    <span className="history-arrow">→</span>
                                    <span className="history-new-value">{change.to}</span>
                                  </>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Timestamps */}
              <div className="detail-field">
                <label>{t('vaultManager.created')}</label>
                <div className="field-value">
                  <span>{new Date(selectedItem.createdAt).toLocaleString()}</span>
                </div>
              </div>

              {selectedItem.updatedAt && selectedItem.updatedAt !== selectedItem.createdAt && (
                <div className="detail-field">
                  <label>{t('vaultManager.lastModified')}</label>
                  <div className="field-value">
                    <span>{new Date(selectedItem.updatedAt).toLocaleString()}</span>
                  </div>
                </div>
              )}
            </div>

            {/* Open and Fill button for login items */}
            {selectedItem.type === 'login' && selectedItem.url && selectedItem.loginMethod !== 'oauth' && (
              <div className="details-action-bar">
                <button 
                  className="btn-open-fill"
                  onClick={() => handleOpenAndFill(selectedItem)}
                >
                  <FiExternalLink size={16} />
                  {t('dataEntry.openAndFill')}
                </button>
              </div>
            )}

            {/* OAuth login action (service login via OAuth) */}
            {selectedItem.type === 'login' && selectedItem.url && selectedItem.loginMethod === 'oauth' && (
              <div className="details-action-bar">
                <button
                  className="btn-open-fill"
                  onClick={() => handleOpenAndOAuthLogin(selectedItem)}
                >
                  <FiExternalLink size={16} />
                  {t('oauth.openAndSignIn')}
                </button>
              </div>
            )}

            {/* Provider account (type oauth) -> open provider login using linked Login credentials */}
            {selectedItem.type === 'oauth' && (selectedItem.oauthProvider || selectedItem.provider) && (
              <div className="details-action-bar">
                <button
                  className="btn-open-fill"
                  onClick={() => handleOpenProviderAccountLogin(selectedItem)}
                >
                  <FiExternalLink size={16} />
                  {t('oauth.openProviderLogin')}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal nuevo item */}
      {showNewItemModal && (
        <div className="modal-overlay" onClick={() => setShowNewItemModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{t('dataEntry.addWhat')}</h2>
              <button onClick={() => setShowNewItemModal(false)}><FiX /></button>
            </div>
            <div className="item-types-grid">
              {itemTypes.map(type => (
                <button
                  key={type.value}
                  className="item-type-card"
                  onClick={() => handleSelectItemType(type.value)}
                >
                  <type.icon size={24} />
                  <span>{type.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Modal nueva bóveda */}
      {showNewVaultModal && (
        <div className="modal-overlay" onClick={() => setShowNewVaultModal(false)}>
          <div className="modal-content modal-small" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{t('vaultManager.createNew')}</h2>
              <button onClick={() => setShowNewVaultModal(false)}><FiX /></button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>{t('vaultManager.vaultName')}</label>
                <input
                  type="text"
                  value={newVaultName}
                  onChange={(e) => setNewVaultName(e.target.value)}
                  placeholder={t('vaultManager.namePlaceholder')}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddVault()}
                  autoFocus
                />
              </div>
              <div className="modal-actions">
                <button className="btn-secondary" onClick={() => setShowNewVaultModal(false)}>
                  {t('common.cancel')}
                </button>
                <button className="btn-primary" onClick={handleAddVault}>
                  {t('vaultManager.create')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal nueva etiqueta */}
      {showNewTagModal && (
        <div className="modal-overlay" onClick={() => setShowNewTagModal(false)}>
          <div className="modal-content modal-small" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{t('vaultManager.newTag')}</h2>
              <button onClick={() => setShowNewTagModal(false)}><FiX /></button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>{t('vaultManager.tagNameLabel')}</label>
                <input
                  type="text"
                  value={newTagName}
                  onChange={(e) => setNewTagName(e.target.value)}
                  placeholder={t('vaultManager.tagPlaceholder')}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddTag()}
                  autoFocus
                />
              </div>
              <div className="modal-actions">
                <button className="btn-secondary" onClick={() => setShowNewTagModal(false)}>
                  {t('common.cancel')}
                </button>
                <button className="btn-primary" onClick={handleAddTag}>
                  {t('vaultManager.createTag')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal formulario de item */}
      {showItemForm && (
        <div className="modal-overlay" onClick={() => setShowItemForm(false)}>
          <div className="modal-content modal-form" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{formData._id ? t('dataEntry.editTitle') : t('dataEntry.newEntry')} {itemTypes.find(t => t.value === newItemType)?.label}</h2>
              <button onClick={() => setShowItemForm(false)}><FiX /></button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>{t('dataEntry.title')} *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => handleFormChange('title', e.target.value)}
                  placeholder={t('dataEntry.titlePlaceholder')}
                  autoFocus
                />
              </div>

              {/* Campos específicos por tipo */}
              {newItemType === 'login' && (
                <>
                  <div className="form-group">
                    <label>{t('dataEntry.username')}</label>
                    <input
                      type="text"
                      value={formData.username}
                      onChange={(e) => handleFormChange('username', e.target.value)}
                      placeholder={t('dataEntry.usernamePlaceholder')}
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('dataEntry.password')}</label>
                    <div className="password-field-with-generator">
                      <input
                        type="password"
                        value={formData.password}
                        onChange={(e) => handleFormChange('password', e.target.value)}
                        placeholder={t('dataEntry.passwordPlaceholder')}
                      />
                      <button
                        type="button"
                        className="btn-generate-password"
                        onClick={() => openPasswordGenerator('password')}
                        title={t('passwordGenerator.title')}
                      >
                        <FiRefreshCw size={16} />
                      </button>
                    </div>
                  </div>
                  <div className="form-group">
                    <label>{t('dataEntry.url')}</label>
                    <input
                      type="url"
                      value={formData.url}
                      onChange={(e) => handleFormChange('url', e.target.value)}
                      placeholder={t('dataEntry.urlPlaceholder')}
                    />
                  </div>
                </>
              )}

              {newItemType === 'card' && (
                <>
                  <div className="form-group">
                    <label>{t('dataEntry.cardHolder')}</label>
                    <input
                      type="text"
                      value={formData.cardHolder}
                      onChange={(e) => handleFormChange('cardHolder', e.target.value)}
                      placeholder={t('dataEntry.cardHolderPlaceholder')}
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('dataEntry.cardNumber')}</label>
                    <input
                      type="text"
                      value={formData.cardNumber}
                      onChange={(e) => handleFormChange('cardNumber', e.target.value)}
                      placeholder={t('dataEntry.cardNumberPlaceholder')}
                      maxLength="19"
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('dataEntry.cardType')}</label>
                    <select
                      value={formData.cardType}
                      onChange={(e) => handleFormChange('cardType', e.target.value)}
                      className="form-select"
                    >
                      <option value="">--</option>
                      <option value="visa">Visa</option>
                      <option value="mastercard">Mastercard</option>
                      <option value="amex">American Express</option>
                      <option value="discover">Discover</option>
                      <option value="diners">Diners Club</option>
                      <option value="jcb">JCB</option>
                      <option value="unionpay">UnionPay</option>
                      <option value="other">{t('common.other')}</option>
                    </select>
                  </div>
                  <div style={{display: 'flex', gap: '12px'}}>
                    <div className="form-group" style={{flex: 1}}>
                      <label>{t('dataEntry.expiryDate')}</label>
                      <input
                        type="text"
                        value={formData.expiryDate}
                        onChange={(e) => handleFormChange('expiryDate', e.target.value)}
                        placeholder={t('dataEntry.expiryPlaceholder')}
                        maxLength="5"
                      />
                    </div>
                    <div className="form-group" style={{flex: 1}}>
                      <label>{t('dataEntry.cvv')}</label>
                      <input
                        type="password"
                        value={formData.cvv}
                        onChange={(e) => handleFormChange('cvv', e.target.value)}
                        placeholder={t('dataEntry.cvvPlaceholder')}
                        maxLength="4"
                      />
                    </div>
                  </div>
                  <div style={{display: 'flex', gap: '12px'}}>
                    <div className="form-group" style={{flex: 1}}>
                      <label>{t('dataEntry.cardPin')}</label>
                      <input
                        type="password"
                        value={formData.cardPin}
                        onChange={(e) => handleFormChange('cardPin', e.target.value)}
                        placeholder="****"
                        maxLength="6"
                      />
                    </div>
                    <div className="form-group" style={{flex: 1}}>
                      <label>{t('dataEntry.issuingBank')}</label>
                      <input
                        type="text"
                        value={formData.issuingBank}
                        onChange={(e) => handleFormChange('issuingBank', e.target.value)}
                        placeholder={t('dataEntry.issuingBank')}
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>{t('dataEntry.billingAddress')}</label>
                    <input
                      type="text"
                      value={formData.billingAddress}
                      onChange={(e) => handleFormChange('billingAddress', e.target.value)}
                      placeholder={t('dataEntry.billingAddress')}
                    />
                  </div>
                </>
              )}

              {newItemType === 'identity' && (
                <>
                  <div style={{display: 'flex', gap: '12px'}}>
                    <div className="form-group" style={{flex: 1}}>
                      <label>{t('dataEntry.firstName')}</label>
                      <input
                        type="text"
                        value={formData.firstName}
                        onChange={(e) => handleFormChange('firstName', e.target.value)}
                        placeholder={t('dataEntry.firstNamePlaceholder')}
                      />
                    </div>
                    <div className="form-group" style={{flex: 1}}>
                      <label>{t('dataEntry.lastName')}</label>
                      <input
                        type="text"
                        value={formData.lastName}
                        onChange={(e) => handleFormChange('lastName', e.target.value)}
                        placeholder={t('dataEntry.lastNamePlaceholder')}
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>{t('dataEntry.email')}</label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleFormChange('email', e.target.value)}
                      placeholder={t('dataEntry.emailPlaceholder')}
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('dataEntry.phone')}</label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => handleFormChange('phone', e.target.value)}
                      placeholder={t('dataEntry.phonePlaceholder')}
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('dataEntry.address')}</label>
                    <input
                      type="text"
                      value={formData.address}
                      onChange={(e) => handleFormChange('address', e.target.value)}
                      placeholder={t('dataEntry.addressPlaceholder')}
                    />
                  </div>
                  <div style={{display: 'flex', gap: '12px'}}>
                    <div className="form-group" style={{flex: 1}}>
                      <label>{t('dataEntry.gender')}</label>
                      <select
                        value={formData.gender}
                        onChange={(e) => handleFormChange('gender', e.target.value)}
                        className="form-select"
                      >
                        <option value="">--</option>
                        <option value="male">{t('dataEntry.genders.male')}</option>
                        <option value="female">{t('dataEntry.genders.female')}</option>
                        <option value="other">{t('common.other')}</option>
                        <option value="preferNotToSay">{t('dataEntry.genders.preferNotToSay')}</option>
                      </select>
                    </div>
                    <div className="form-group" style={{flex: 1}}>
                      <label>{t('dataEntry.birthDate')}</label>
                      <input
                        type="date"
                        value={formData.birthDate}
                        onChange={(e) => handleFormChange('birthDate', e.target.value)}
                      />
                    </div>
                  </div>
                  <div style={{display: 'flex', gap: '12px'}}>
                    <div className="form-group" style={{flex: 1}}>
                      <label>{t('dataEntry.company')}</label>
                      <input
                        type="text"
                        value={formData.company}
                        onChange={(e) => handleFormChange('company', e.target.value)}
                        placeholder={t('dataEntry.company')}
                      />
                    </div>
                    <div className="form-group" style={{flex: 1}}>
                      <label>{t('dataEntry.department')}</label>
                      <input
                        type="text"
                        value={formData.department}
                        onChange={(e) => handleFormChange('department', e.target.value)}
                        placeholder={t('dataEntry.department')}
                      />
                    </div>
                  </div>
                  <div style={{display: 'flex', gap: '12px'}}>
                    <div className="form-group" style={{flex: 1}}>
                      <label>{t('dataEntry.jobTitle')}</label>
                      <input
                        type="text"
                        value={formData.jobTitle}
                        onChange={(e) => handleFormChange('jobTitle', e.target.value)}
                        placeholder={t('dataEntry.jobTitle')}
                      />
                    </div>
                    <div className="form-group" style={{flex: 1}}>
                      <label>{t('dataEntry.occupation')}</label>
                      <input
                        type="text"
                        value={formData.occupation}
                        onChange={(e) => handleFormChange('occupation', e.target.value)}
                        placeholder={t('dataEntry.occupation')}
                      />
                    </div>
                  </div>
                  <div style={{display: 'flex', gap: '12px'}}>
                    <div className="form-group" style={{flex: 1}}>
                      <label>{t('dataEntry.socialSecurity')}</label>
                      <input
                        type="password"
                        value={formData.socialSecurity}
                        onChange={(e) => handleFormChange('socialSecurity', e.target.value)}
                        placeholder="***-**-****"
                      />
                    </div>
                    <div className="form-group" style={{flex: 1}}>
                      <label>{t('dataEntry.passportNumber')}</label>
                      <input
                        type="text"
                        value={formData.passportNumber}
                        onChange={(e) => handleFormChange('passportNumber', e.target.value)}
                        placeholder={t('dataEntry.passportNumber')}
                      />
                    </div>
                  </div>
                  <div style={{display: 'flex', gap: '12px'}}>
                    <div className="form-group" style={{flex: 1}}>
                      <label>{t('dataEntry.licenseNumber')}</label>
                      <input
                        type="text"
                        value={formData.licenseNumber}
                        onChange={(e) => handleFormChange('licenseNumber', e.target.value)}
                        placeholder={t('dataEntry.licenseNumber')}
                      />
                    </div>
                    <div className="form-group" style={{flex: 1}}>
                      <label>{t('dataEntry.website')}</label>
                      <input
                        type="url"
                        value={formData.website}
                        onChange={(e) => handleFormChange('website', e.target.value)}
                        placeholder="https://"
                      />
                    </div>
                  </div>
                </>
              )}

              {newItemType === 'password' && (
                <div className="form-group">
                  <label>{t('dataEntry.password')}</label>
                  <div className="password-field-with-generator">
                    <input
                      type="password"
                      value={formData.password}
                      onChange={(e) => handleFormChange('password', e.target.value)}
                      placeholder={t('dataEntry.passwordPlaceholder')}
                    />
                    <button
                      type="button"
                      className="btn-generate-password"
                      onClick={() => openPasswordGenerator('password')}
                      title={t('passwordGenerator.title')}
                    >
                      <FiRefreshCw size={16} />
                    </button>
                  </div>
                </div>
              )}

              {newItemType === 'ssh' && (
                <div className="form-group">
                  <label>{t('dataEntry.sshKey')}</label>
                  <textarea
                    value={formData.sshKey}
                    onChange={(e) => handleFormChange('sshKey', e.target.value)}
                    placeholder={t('dataEntry.sshKeyPlaceholder')}
                    rows="6"
                    className="form-textarea"
                    style={{fontFamily: 'monospace', fontSize: '12px'}}
                  />
                </div>
              )}

              {newItemType === 'api' && (
                <>
                  <div className="form-group">
                    <label>{t('dataEntry.apiKey')}</label>
                    <input
                      type="text"
                      value={formData.apiKey}
                      onChange={(e) => handleFormChange('apiKey', e.target.value)}
                      placeholder={t('dataEntry.apiKeyPlaceholder')}
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('dataEntry.apiSecret')}</label>
                    <input
                      type="password"
                      value={formData.apiSecret}
                      onChange={(e) => handleFormChange('apiSecret', e.target.value)}
                      placeholder={t('dataEntry.apiSecretPlaceholder')}
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('dataEntry.endpointUrl')}</label>
                    <input
                      type="url"
                      value={formData.url}
                      onChange={(e) => handleFormChange('url', e.target.value)}
                      placeholder={t('dataEntry.endpointUrlPlaceholder')}
                    />
                  </div>
                </>
              )}

              {newItemType === 'database' && (
                <>
                  <div className="form-group">
                    <label>{t('dataEntry.host')}</label>
                    <input
                      type="text"
                      value={formData.dbHost}
                      onChange={(e) => handleFormChange('dbHost', e.target.value)}
                      placeholder={t('dataEntry.hostPlaceholder')}
                    />
                  </div>
                  <div style={{display: 'flex', gap: '12px'}}>
                    <div className="form-group" style={{flex: 2}}>
                      <label>{t('dataEntry.database')}</label>
                      <input
                        type="text"
                        value={formData.dbName}
                        onChange={(e) => handleFormChange('dbName', e.target.value)}
                        placeholder={t('dataEntry.databasePlaceholder')}
                      />
                    </div>
                    <div className="form-group" style={{flex: 1}}>
                      <label>{t('dataEntry.port')}</label>
                      <input
                        type="text"
                        value={formData.dbPort}
                        onChange={(e) => handleFormChange('dbPort', e.target.value)}
                        placeholder={t('dataEntry.portPlaceholder')}
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>{t('dataEntry.user')}</label>
                    <input
                      type="text"
                      value={formData.username}
                      onChange={(e) => handleFormChange('username', e.target.value)}
                      placeholder={t('dataEntry.userPlaceholder')}
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('dataEntry.password')}</label>
                    <div className="password-field-with-generator">
                      <input
                        type="password"
                        value={formData.password}
                        onChange={(e) => handleFormChange('password', e.target.value)}
                        placeholder={t('dataEntry.passwordPlaceholder')}
                      />
                      <button
                        type="button"
                        className="btn-generate-password"
                        onClick={() => openPasswordGenerator('password')}
                        title={t('passwordGenerator.title')}
                      >
                        <FiRefreshCw size={16} />
                      </button>
                    </div>
                  </div>
                </>
              )}

              {newItemType === 'file' && (
                <div className="form-group">
                  <label>{t('dataEntry.file')}</label>
                  <input
                    type="file"
                    onChange={(e) => handleFileChange(e)}
                    className="form-input-file"
                  />
                  {formData.fileName && (
                    <div style={{marginTop: '8px', fontSize: '13px', color: '#888'}}>
                      {t('dataEntry.fileAttached')} {formData.fileName}
                    </div>
                  )}
                </div>
              )}

              {newItemType === 'oauth' && (
                <>
                  <div className="form-group">
                    <label>{t('oauth.selectProvider')} *</label>
                    <select
                      value={formData.oauthProvider}
                      onChange={(e) => handleFormChange('oauthProvider', e.target.value)}
                      className="form-select"
                    >
                      <option value="">{t('oauth.selectProvider')}</option>
                      {oauthProviders.map(provider => (
                        <option key={provider.value} value={provider.value}>
                          {provider.icon} {provider.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>{t('dataEntry.email')}</label>
                    <input
                      type="email"
                      value={formData.oauthEmail}
                      onChange={(e) => handleFormChange('oauthEmail', e.target.value)}
                      placeholder={t('dataEntry.emailPlaceholder')}
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('dataEntry.username')} ({t('common.optional')})</label>
                    <input
                      type="text"
                      value={formData.oauthUsername}
                      onChange={(e) => handleFormChange('oauthUsername', e.target.value)}
                      placeholder={t('dataEntry.usernamePlaceholder')}
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('dataEntry.url')} ({t('common.optional')})</label>
                    <input
                      type="url"
                      value={formData.url}
                      onChange={(e) => handleFormChange('url', e.target.value)}
                      placeholder="https://accounts.google.com"
                    />
                  </div>
                </>
              )}

              {newItemType === 'subscription' && (
                <>
                  <div className="form-group">
                    <label>{t('dataEntry.provider')}</label>
                    <input
                      type="text"
                      value={formData.provider}
                      onChange={(e) => handleFormChange('provider', e.target.value)}
                      placeholder={t('dataEntry.provider')}
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('dataEntry.plan')}</label>
                    <input
                      type="text"
                      value={formData.plan}
                      onChange={(e) => handleFormChange('plan', e.target.value)}
                      placeholder={t('dataEntry.plan')}
                    />
                  </div>
                  <div style={{display: 'flex', gap: '12px'}}>
                    <div className="form-group" style={{flex: 2}}>
                      <label>{t('dataEntry.cost')}</label>
                      <input
                        type="number"
                        step="0.01"
                        value={formData.cost}
                        onChange={(e) => handleFormChange('cost', e.target.value)}
                        placeholder="0.00"
                      />
                    </div>
                    <div className="form-group" style={{flex: 1}}>
                      <label>{t('dataEntry.currency')}</label>
                      <select
                        value={formData.currency}
                        onChange={(e) => handleFormChange('currency', e.target.value)}
                        className="form-select"
                      >
                        <option value="EUR">EUR €</option>
                        <option value="USD">USD $</option>
                        <option value="GBP">GBP £</option>
                        <option value="JPY">JPY ¥</option>
                        <option value="CHF">CHF</option>
                        <option value="CAD">CAD $</option>
                        <option value="AUD">AUD $</option>
                        <option value="CNY">CNY ¥</option>
                      </select>
                    </div>
                  </div>
                  <div className="form-group">
                    <label>{t('dataEntry.billingCycle')}</label>
                    <select
                      value={formData.billingCycle}
                      onChange={(e) => handleFormChange('billingCycle', e.target.value)}
                      className="form-select"
                    >
                      <option value="monthly">{t('dataEntry.billingCycles.monthly')}</option>
                      <option value="quarterly">{t('dataEntry.billingCycles.quarterly')}</option>
                      <option value="semiannual">{t('dataEntry.billingCycles.semiannual')}</option>
                      <option value="annual">{t('dataEntry.billingCycles.annual')}</option>
                      <option value="lifetime">{t('dataEntry.billingCycles.lifetime')}</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>{t('dataEntry.status')}</label>
                    <select
                      value={formData.subscriptionStatus}
                      onChange={(e) => handleFormChange('subscriptionStatus', e.target.value)}
                      className="form-select"
                    >
                      <option value="active">{t('dataEntry.subscriptionStatuses.active')}</option>
                      <option value="trial">{t('dataEntry.subscriptionStatuses.trial')}</option>
                      <option value="paused">{t('dataEntry.subscriptionStatuses.paused')}</option>
                      <option value="cancelled">{t('dataEntry.subscriptionStatuses.cancelled')}</option>
                      <option value="expired">{t('dataEntry.subscriptionStatuses.expired')}</option>
                    </select>
                  </div>
                  <div style={{display: 'flex', gap: '12px'}}>
                    <div className="form-group" style={{flex: 1}}>
                      <label>{t('dataEntry.startDate')}</label>
                      <input
                        type="date"
                        value={formData.startDate}
                        onChange={(e) => handleFormChange('startDate', e.target.value)}
                      />
                    </div>
                    <div className="form-group" style={{flex: 1}}>
                      <label>{t('dataEntry.renewalDate')}</label>
                      <input
                        type="date"
                        value={formData.renewalDate}
                        onChange={(e) => handleFormChange('renewalDate', e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <div className="switch-container">
                      <label className="switch-label">
                        <input
                          type="checkbox"
                          checked={formData.autoRenew}
                          onChange={(e) => handleFormChange('autoRenew', e.target.checked)}
                        />
                        <span className="switch-slider"></span>
                      </label>
                      <span className="switch-text">{t('dataEntry.autoRenew')}</span>
                    </div>
                  </div>
                  <div className="form-group">
                    <label>{t('dataEntry.url')}</label>
                    <input
                      type="url"
                      value={formData.url}
                      onChange={(e) => handleFormChange('url', e.target.value)}
                      placeholder={t('dataEntry.urlPlaceholder')}
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('dataEntry.username')}</label>
                    <input
                      type="text"
                      value={formData.username}
                      onChange={(e) => handleFormChange('username', e.target.value)}
                      placeholder={t('dataEntry.usernamePlaceholder')}
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('dataEntry.password')}</label>
                    <div className="password-field-with-generator">
                      <input
                        type="password"
                        value={formData.password}
                        onChange={(e) => handleFormChange('password', e.target.value)}
                        placeholder={t('dataEntry.passwordPlaceholder')}
                      />
                      <button
                        type="button"
                        className="btn-generate-password"
                        onClick={() => openPasswordGenerator('password')}
                        title={t('passwordGenerator.title')}
                      >
                        <FiRefreshCw size={16} />
                      </button>
                    </div>
                  </div>
                </>
              )}

              {/* Multiple Use Switch - for login and oauth types */}
              {(newItemType === 'login' || newItemType === 'oauth') && (
                <div className="form-group multiple-use-section">
                  <div className="switch-container">
                    <label className="switch-label">
                      <input
                        type="checkbox"
                        checked={formData.multipleUse}
                        onChange={(e) => handleFormChange('multipleUse', e.target.checked)}
                      />
                      <span className="switch-slider"></span>
                    </label>
                    <span className="switch-text">
                      {newItemType === 'login' 
                        ? t('oauth.alsoUsableAsOAuth')
                        : t('oauth.alsoUsableAsLogin')
                      }
                    </span>
                  </div>
                  <p className="form-help-text">
                    {newItemType === 'login'
                      ? t('oauth.multipleUseLoginHelp')
                      : t('oauth.multipleUseOAuthHelp')
                    }
                  </p>
                </div>
              )}

              <div className="form-group">
                <label>{t('dataEntry.vaultLabel')}</label>
                <select
                  value={formData.vault}
                  onChange={(e) => handleFormChange('vault', e.target.value)}
                  className="form-select"
                >
                  {Array.isArray(vaults) && vaults.filter(v => !v.isDefault).map(vault => (
                    <option key={vault.id} value={vault.id}>{vault.name}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>{t('dataEntry.tagsLabel')}</label>
                <div className="tags-input">
                  {Array.isArray(formData.tags) && formData.tags.map(tag => (
                    <span key={tag} className="tag-badge">
                      {tag}
                      <button type="button" onClick={() => handleRemoveTagFromItem(tag)}><FiX size={12} /></button>
                    </span>
                  ))}
                  <input
                    type="text"
                    placeholder={t('dataEntry.tagsPlaceholder')}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        if (e.target.value.trim()) {
                          handleAddTagToItem(e.target.value.trim());
                          e.target.value = '';
                        }
                      }
                    }}
                  />
                </div>
              </div>

              {/* Expiration date field - available for all types */}
              {newItemType && newItemType !== 'note' && newItemType !== 'file' && (
                <div className="form-group">
                  <label>{t('dataEntry.expirationDate')}</label>
                  <input
                    type="date"
                    value={formData.expirationDate}
                    onChange={(e) => handleFormChange('expirationDate', e.target.value)}
                  />
                </div>
              )}

              {/* Scheduled alerts */}
              {newItemType && newItemType !== 'note' && (
                <div className="form-group alerts-form-section">
                  <label style={{display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px'}}>
                    <FiBell size={14} /> {t('dataEntry.expirationAlerts')}
                  </label>
                  {Array.isArray(formData.alerts) && formData.alerts.map((alert, index) => (
                    <div key={index} className="alert-form-row" style={{display: 'flex', gap: '8px', marginBottom: '8px', alignItems: 'flex-end'}}>
                      <div className="form-group" style={{flex: 1, marginBottom: 0}}>
                        <input
                          type="date"
                          value={alert.date}
                          onChange={(e) => handleUpdateAlert(index, 'date', e.target.value)}
                        />
                      </div>
                      <div className="form-group" style={{flex: 2, marginBottom: 0}}>
                        <input
                          type="text"
                          value={alert.message}
                          onChange={(e) => handleUpdateAlert(index, 'message', e.target.value)}
                          placeholder={t('dataEntry.alertMessage')}
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRemoveAlert(index)}
                        style={{padding: '10px 14px', background: 'rgba(255,0,0,0.1)', border: '1px solid rgba(255,0,0,0.3)', borderRadius: '6px', color: '#ff6b6b', cursor: 'pointer'}}
                      >
                        <FiX size={14} />
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={handleAddAlert}
                    style={{
                      padding: '8px 14px',
                      background: 'rgba(255,255,255,0.05)',
                      border: '1px dashed rgba(255,255,255,0.2)',
                      borderRadius: '6px',
                      color: '#888',
                      cursor: 'pointer',
                      fontSize: '12px'
                    }}
                  >
                    + {t('dataEntry.addAlert')}
                  </button>
                </div>
              )}

              {/* Custom fields - rendered like default fields */}
              {Array.isArray(formData.customFields) && formData.customFields.length > 0 && (
                <>
                  {formData.customFields.map((field, index) => (
                    <div key={index} className="form-group unified-field">
                      <div className="unified-field-header">
                        <input
                          type="text"
                          value={field.label}
                          onChange={(e) => handleUpdateCustomField(index, 'label', e.target.value)}
                          placeholder={t('dataEntry.fieldName')}
                          className="unified-field-label-input"
                        />
                        <select
                          value={field.type}
                          onChange={(e) => handleUpdateCustomField(index, 'type', e.target.value)}
                          className="unified-field-type-select"
                        >
                          <option value="text">{t('dataEntry.fieldTypes.text')}</option>
                          <option value="password">{t('dataEntry.fieldTypes.password')}</option>
                          <option value="url">{t('dataEntry.fieldTypes.url')}</option>
                          <option value="email">{t('dataEntry.fieldTypes.email')}</option>
                          <option value="tel">{t('dataEntry.fieldTypes.tel')}</option>
                          <option value="textarea">{t('dataEntry.fieldTypes.textarea')}</option>
                          <option value="file">{t('dataEntry.fieldTypes.file')}</option>
                        </select>
                        <button
                          type="button"
                          className="unified-field-remove-btn"
                          onClick={() => handleRemoveCustomField(index)}
                          title={t('dataEntry.removeField')}
                        >
                          <FiX size={14} />
                        </button>
                      </div>
                      {field.type === 'file' ? (
                        <>
                          <input
                            type="file"
                            onChange={(e) => handleFileChange(e, true, index)}
                            className="form-input-file"
                          />
                          {field.fileName && (
                            <div style={{marginTop: '4px', fontSize: '12px', color: '#888'}}>
                              📎 {field.fileName}
                            </div>
                          )}
                        </>
                      ) : field.type === 'textarea' ? (
                        <textarea
                          value={field.value}
                          onChange={(e) => handleUpdateCustomField(index, 'value', e.target.value)}
                          placeholder={field.label || t('dataEntry.fieldValue')}
                          rows="2"
                          className="form-textarea"
                        />
                      ) : field.type === 'password' ? (
                        <div className="password-field-with-generator">
                          <input
                            type="password"
                            value={field.value}
                            onChange={(e) => handleUpdateCustomField(index, 'value', e.target.value)}
                            placeholder={field.label || t('dataEntry.fieldValue')}
                          />
                          <button
                            type="button"
                            className="btn-generate-password"
                            onClick={() => openPasswordGenerator(`custom_${index}`)}
                            title={t('passwordGenerator.title')}
                          >
                            <FiRefreshCw size={16} />
                          </button>
                        </div>
                      ) : (
                        <input
                          type={field.type}
                          value={field.value}
                          onChange={(e) => handleUpdateCustomField(index, 'value', e.target.value)}
                          placeholder={field.label || t('dataEntry.fieldValue')}
                        />
                      )}
                    </div>
                  ))}
                </>
              )}

              <button
                type="button"
                onClick={handleAddCustomField}
                style={{
                  width: '100%',
                  padding: '10px',
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px dashed rgba(255,255,255,0.2)',
                  borderRadius: '6px',
                  color: '#888',
                  cursor: 'pointer',
                  marginBottom: '16px',
                  fontSize: '13px',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = 'rgba(255,255,255,0.08)';
                  e.target.style.borderColor = 'rgba(255,255,255,0.3)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'rgba(255,255,255,0.05)';
                  e.target.style.borderColor = 'rgba(255,255,255,0.2)';
                }}
              >
                + {t('dataEntry.addCustomField')}
              </button>

              <div className="form-group">
                <label>{t('dataEntry.notes')}</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => handleFormChange('notes', e.target.value)}
                  placeholder={t('dataEntry.notesPlaceholder')}
                  rows="4"
                  className="form-textarea"
                />
              </div>

              <div className="modal-actions">
                <button className="btn-secondary" onClick={() => setShowItemForm(false)}>
                  {t('common.cancel')}
                </button>
                <button className="btn-primary" onClick={handleSaveItem}>
                  {t('common.save')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Password Generator Modal */}
      {showPasswordGenerator && (
        <PasswordGenerator
          onSelect={handlePasswordGenerated}
          onClose={() => {
            setShowPasswordGenerator(false);
            setPasswordGeneratorTarget(null);
          }}
        />
      )}

      {/* Import/Export Modal */}
      {showImportExport && (
        <ImportExport
          onClose={() => setShowImportExport(false)}
          onImportComplete={fetchItems}
        />
      )}

      {/* Extension Update Modal */}
      {showExtensionUpdate && (
        <ExtensionUpdate
          onClose={() => setShowExtensionUpdate(false)}
          onStatusChange={setHasExtensionUpdate}
        />
      )}

      {/* OTP Manager Modal */}
      {showOTPManager && (
        <div className="modal-overlay" onClick={() => setShowOTPManager(false)}>
          <div className="modal-content otp-manager-modal" onClick={e => e.stopPropagation()}>
            <OTPManager
              onClose={() => setShowOTPManager(false)}
            />
          </div>
        </div>
      )}

      {/* Smart Card Backup Modal */}
      {showSmartCardBackup && (
        <div className="modal-overlay" onClick={() => setShowSmartCardBackup(false)}>
          <div className="modal-content smartcard-backup-modal" onClick={e => e.stopPropagation()}>
            <SmartCardBackup
              onClose={() => setShowSmartCardBackup(false)}
            />
          </div>
        </div>
      )}

      {/* Realtime Notifications Panel */}
      {showRealtimeNotifications && (
        <div className="modal-overlay" onClick={() => setShowRealtimeNotifications(false)}>
          <div className="modal-content notifications-modal" onClick={e => e.stopPropagation()}>
            <RealtimeNotifications
              onClose={() => setShowRealtimeNotifications(false)}
            />
          </div>
        </div>
      )}

      {/* Wipe Vault Confirmation Modal */}
      {showWipeConfirm && (
        <div className="modal-overlay" onClick={() => setShowWipeConfirm(false)}>
          <div className="modal-content delete-confirm-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{t('common.deleteAllVaultTitle')}</h2>
              <button onClick={() => setShowWipeConfirm(false)}><FiX /></button>
            </div>
            <div className="delete-confirm-body">
              <FiTrash2 size={48} className="delete-icon" />
              <p style={{ fontWeight: 'bold', color: '#ff4444' }}>{t('common.deleteAllVaultMessage')}</p>
              <p style={{ fontSize: '0.85em', opacity: 0.7 }}>{t('common.deleteAllVaultWarning')}</p>
              <div className="delete-confirm-actions">
                <button 
                  className="btn-secondary" 
                  onClick={() => setShowWipeConfirm(false)}
                  disabled={wipingVault}
                >
                  {t('common.cancel')}
                </button>
                <button 
                  className="btn-danger" 
                  onClick={handleDeleteAllVault}
                  disabled={wipingVault}
                >
                  <FiTrash2 size={16} /> {wipingVault ? t('common.deleting') : t('common.deleteAll')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="modal-overlay" onClick={() => { setShowDeleteConfirm(false); setItemToDelete(null); }}>
          <div className="modal-content delete-confirm-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{t('common.deleteConfirmTitle')}</h2>
              <button onClick={() => { setShowDeleteConfirm(false); setItemToDelete(null); }}><FiX /></button>
            </div>
            <div className="delete-confirm-body">
              <FiTrash2 size={48} className="delete-icon" />
              {itemToDelete ? (
                <p>{t('common.deleteConfirmMessage')}</p>
              ) : selectedItems.size > 0 ? (
                <p>{t('common.deleteSelectedMessage', { count: selectedItems.size })}</p>
              ) : null}
              <div className="delete-confirm-actions">
                <button 
                  className="btn-secondary" 
                  onClick={() => { setShowDeleteConfirm(false); setItemToDelete(null); }}
                >
                  {t('common.cancel')}
                </button>
                <button 
                  className="btn-danger" 
                  onClick={() => itemToDelete ? handleDeleteItem(itemToDelete) : handleDeleteSelected()}
                >
                  <FiTrash2 size={16} /> {t('common.delete')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Vault;
